/**
 * Route-driven team workspace and governance screen. Loads membership, linked emails, and
 * discoverable teams, then hydrates the selected workspace. Handles invitations/join approvals,
 * role and policy controls, projects/bundles/activities, custom fields, feeds, mail provisioning,
 * and external connections. Extended dashboards load by section; admin UI gates do not substitute
 * for server permissions. Team reports use the selected workspace rather than personal task-store
 * data.
 *
 * @module apps/web/src/pages/TeamsPage
 */

import type {
  LinkedEmail,
  MailProvisioningOutcome,
  TeamActivityView,
  TeamCustomField,
  TeamDiscoveryResult,
  TeamMember,
  TeamWithMembership,
  TeamWorkspaceData,
  WorkspaceBundle,
  WorkspaceProject,
} from '@fluxability/types';
// React's Activity, renamed because lucide's Activity icon is also imported. Every team section
// stays mounted (hidden) after it first renders, so switching sections reveals it instead of
// mounting it again.
import { Activity as RetainedSection, lazy, Suspense, useEffect, useEffectEvent, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Activity,
  AtSign,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  Cloud,
  Copy,
  FolderKanban,
  GitBranch,
  Globe2,
  Link,
  Lock,
  Mail,
  Plus,
  RefreshCw,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserCheck,
  UserPlus,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { afterNextPaint } from '@/lib/afterPaint';
import { groupBundlesByProject } from '@/lib/bundleGroups';
import { cn } from '@/lib/utils';
import { TeamBrandIdentity } from '@/components/teams/TeamBrandIdentity';
import { TeamOverview } from '@/components/teams/TeamOverview';
import { AssigneeSuggestions } from '@/components/teams/AssigneeSuggestions';
import { UserAvatar } from '@/components/ui/user-avatar';
/** Lazy-load the extended team panels when the Teams screen renders one. The Suspense boundary owns loading presentation; team capability props still come from the loaded workspace. */
const TeamExtendedSection = lazy(() => import('@/components/teams/TeamExtendedSection'));
/** Section keys delegated to the lazy-loaded extended-team panel bundle. */
const extendedSections = ['project-drafts', 'integrations', 'hours', 'timesheets', 'meetings', 'activity-proposals', 'calendar', 'branding'];

/** Team chooser/workspace load state and user-facing request status for the current Teams screen. */
type TeamState = {
  /** Teams in which the current account has membership. */
  teams: TeamWithMembership[];
  /** Teams returned by discovery with allowed join actions. */
  discoverableTeams: TeamDiscoveryResult[];
  /** Linked account addresses used to match membership and invitation eligibility. */
  accountEmails: LinkedEmail[];
  /** Selected team's aggregate workspace response when available. */
  workspace: TeamWorkspaceData | null;
  /** Whether the team-page loading operation is in progress. */
  loading: boolean;
  /** Currently selected team whose workspace is displayed. */
  selectedTeamId: string;
  /** User-facing status text from the most recent page operation. */
  status: string;
};

/** Canonical team-route section identifiers, separate from retained aliases and DOM disclosure IDs. */
type TeamSectionKey =
  | 'project-drafts'
  | 'hours' | 'timesheets' | 'meetings' | 'activity-proposals' | 'calendar' | 'branding'
  | 'members'
  | 'access'
  | 'activity'
  | 'projects-bundles'
  | 'fields'
  | 'intake'
  | 'integrations';

/** Team navigation metadata linking each route section to its label, icon, and disclosures that must open. */
const teamSections: Array<{
  /** Stable navigation-group identifier used in the team dashboard. */
  id: TeamSectionKey;
  /** Visible navigation-group title. */
  label: string;
  /** Short explanation of the tools in this navigation group. */
  description: string;
  /** Lucide icon identifying the navigation group. */
  icon: LucideIcon;
  /** Panel identifiers included in this dashboard navigation group. */
  sectionIds: string[];
}> = [
  { id: 'project-drafts', label: 'Project drafts', description: 'Review unpublished project stages and activities.', icon: FolderKanban, sectionIds: [] },
  { id: 'hours', label: 'Work hours', description: 'Weekly availability and dated overrides.', icon: CalendarDays, sectionIds: [] },
  { id: 'timesheets', label: 'Timesheets', description: 'Recorded time with audited corrections.', icon: Activity, sectionIds: [] },
  { id: 'meetings', label: 'Meetings', description: 'Find shared availability and book meetings.', icon: UsersRound, sectionIds: [] },
  { id: 'activity-proposals', label: 'Proposals', description: 'Review activities suggested from mail.', icon: Mail, sectionIds: [] },
  { id: 'calendar', label: 'Calendar', description: 'Calendar visibility for this team.', icon: CalendarDays, sectionIds: [] },
  { id: 'branding', label: 'Identity', description: 'Workspace identity and palette.', icon: SlidersHorizontal, sectionIds: [] },
  {
    id: 'members',
    label: 'Members',
    description: 'Invite, provision, and review team members.',
    icon: Shield,
    sectionIds: ['users'],
  },
  {
    id: 'access',
    label: 'Access',
    description: 'Visibility, join rules, and pending access requests.',
    icon: ShieldCheck,
    sectionIds: ['governance', 'requests'],
  },
  {
    id: 'activity',
    label: 'Activity',
    description: 'Assign team activities into personal schedulers.',
    icon: Activity,
    sectionIds: ['activities'],
  },
  {
    id: 'projects-bundles',
    label: 'Projects + Bundles',
    description: 'Create and list project structure for this team.',
    icon: FolderKanban,
    sectionIds: ['structure', 'projects'],
  },
  {
    id: 'fields',
    label: 'Fields',
    description: 'Manage custom fields required by team activities.',
    icon: SlidersHorizontal,
    sectionIds: ['fields'],
  },
  {
    id: 'intake',
    label: 'Intake',
    description: 'Turn email rules into team activity proposals.',
    icon: Mail,
    sectionIds: ['intake'],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    description: 'Connect calendars and other Fluxability accounts.',
    icon: CalendarDays,
    sectionIds: ['integrations'],
  },
];

/** Compatibility map from old route terms to canonical team sections; it preserves existing deep links. */
const teamSectionAliases: Record<string, TeamSectionKey> = {
  'project-drafts': 'project-drafts',
  hours: 'hours', timesheets: 'timesheets', meetings: 'meetings', 'activity-proposals': 'activity-proposals', calendar: 'calendar', branding: 'branding', people: 'members', connections: 'integrations', settings: 'access',
  users: 'members',
  members: 'members',
  governance: 'access',
  requests: 'access',
  access: 'access',
  activities: 'activity',
  activity: 'activity',
  projects: 'projects-bundles',
  bundles: 'projects-bundles',
  'projects-bundles': 'projects-bundles',
  fields: 'fields',
  intake: 'intake',
  integrations: 'integrations',
};

/** Available personal/team synchronization modes presented for server policy selection. */
const syncPolicies = [
  { value: 'private', label: 'Private' },
  { value: 'one_way_in', label: 'One-way in' },
  { value: 'two_way', label: 'Two-way' },
];

/** Team discovery/join visibility choices and their explanatory labels. */
const teamVisibilityOptions = [
  { value: 'public', label: 'Public', description: 'Visible and joinable by matching people.' },
  { value: 'invite_only', label: 'Invite-only', description: 'Visible to matching people; joining requires approval.' },
  { value: 'private', label: 'Private', description: 'Hidden unless someone is invited.' },
];

/** Calendar-feed disclosure levels offered to the owner; the server enforces the chosen resource scope. */
const feedPermissions = [
  { value: 'busy_free', label: 'Busy/free only' },
  { value: 'team_names', label: 'Team activity names' },
  { value: 'team_details', label: 'Team project + bundle' },
  { value: 'full_details', label: 'Full activity details' },
];

/** Google Calendar detail-disclosure levels presented for team connection settings. */
const gcalVisibilityOptions = [
  { value: 'busy_free', label: 'Busy/free only' },
  { value: 'event_titles', label: 'Event titles' },
  { value: 'full_details', label: 'Full event details' },
];

/** Initial disclosure preferences before the active route expands its required panels. */
const defaultCollapsedSections: Record<string, boolean> = {
  createTeam: true,
  accountEmails: true,
  joinTeams: true,
  structure: true,
  governance: true,
  users: true,
  requests: true,
  activities: true,
  projects: true,
  overview: true,
  fields: true,
  intake: true,
  integrations: true,
};

/**
 * Route-driven team workspace and governance screen. Loads membership, linked emails, and
 * discoverable teams, then hydrates the selected workspace. Handles invitations/join approvals,
 * role and policy controls, projects/bundles/activities, custom fields, feeds, mail provisioning,
 * and external connections. Extended dashboards load by section; admin UI gates do not substitute
 * for server permissions. Team reports use the selected workspace rather than personal task-store
 * data.
 */
export function TeamsPage() {
  const navigate = useNavigate();
  const { teamId: routeTeamId, section: routeSectionParam } = useParams();
  const [searchParams] = useSearchParams();
  const legacyQueryTeamId = searchParams.get('team') || '';
  const routeSection = normalizeTeamSection(routeSectionParam);
  const requestedTeamId = routeTeamId || legacyQueryTeamId;
  const [state, setState] = useState<TeamState>({
    teams: [],
    discoverableTeams: [],
    accountEmails: [],
    workspace: null,
    loading: true,
    selectedTeamId: '',
    status: '',
  });
  const [teamDraft, setTeamDraft] = useState({ name: '', domain: '', purpose: '', membership_email: '' });
  const [teamGovernanceDraft, setTeamGovernanceDraft] = useState({
    visibility: 'invite_only',
    parent_team_id: 'none',
    connection_mode: 'open',
    allowed_connection_domains: '',
  });
  const [emailDraft, setEmailDraft] = useState({ email: '' });
  const [memberDraft, setMemberDraft] = useState({
    email: '',
    display_name: '',
    role: 'member',
    create_mail_account: true,
    mail_role: 'user',
  });
  const [joinRuleDraft, setJoinRuleDraft] = useState({ pattern: '', role: 'member', auto_approve: false });
  const [projectDraft, setProjectDraft] = useState({ name: '', purpose: '', priority_score: '5' });
  const [editingProjectId, setEditingProjectId] = useState('');
  const [editingProjectDraft, setEditingProjectDraft] = useState({ name: '', purpose: '', priority_score: '5' });
  const [bundleDraft, setBundleDraft] = useState({
    name: '',
    project_id: 'none',
    purpose: '',
    priority_score: '5',
  });
  const [editingBundleId, setEditingBundleId] = useState('');
  const [editingBundleDraft, setEditingBundleDraft] = useState({
    name: '',
    project_id: 'none',
    purpose: '',
    priority_score: '5',
  });
  const [activityDraft, setActivityDraft] = useState({
    title: '',
    description: '',
    assigned_to_user_id: '',
    bundle_id: '',
    due_date: '',
    duration_minutes: '45',
    priority: 'medium',
    assignee_decides_duration: false,
  });
  const [activityAttributeValues, setActivityAttributeValues] = useState<Record<string, string>>({});
  const [attributeDraft, setAttributeDraft] = useState({
    name: '',
    data_type: 'float',
    specificity: '5',
    field_weight: '1',
    required: false,
    options: '',
  });
  const [ruleDraft, setRuleDraft] = useState({
    rule_type: 'keyword',
    pattern: '',
    action: 'propose_activity',
    project_id: 'none',
    bundle_id: 'none',
  });
  const [feedDraft, setFeedDraft] = useState({
    permission: 'busy_free',
    label: '',
    include_gcal: false,
    gcal_visibility: 'busy_free',
    calendar_ids: '',
  });
  const [connectionDraft, setConnectionDraft] = useState({
    provider: 'flexiplanner',
    remote_domain: '',
    direction: 'one_way_in',
    remote_account_email: '',
    team_visibility: 'team_names',
    share_gcal: true,
    gcal_visibility: 'busy_free',
    calendar_ids: '',
  });
  const [latestFeedUrl, setLatestFeedUrl] = useState('');
  // Sections opened so far ('overview' for the team overview); each stays mounted (hidden) after
  // its first visit, so returning to it only reveals it. Unvisited sections are not rendered.
  const sectionKey = routeSection ?? 'overview';
  const [visitedSections, setVisitedSections] = useState<string[]>(() => [sectionKey]);
  if (!visitedSections.includes(sectionKey)) setVisitedSections([...visitedSections, sectionKey]);
  // Extended sections opened so far; each stays mounted (hidden) so returning to it only reveals it.
  const [visitedExtended, setVisitedExtended] = useState<string[]>([]);
  if (routeSection && extendedSections.includes(routeSection) && !visitedExtended.includes(routeSection))
    setVisitedExtended([...visitedExtended, routeSection]);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(
    () => initialCollapsedSections(routeSection)
  );

  const workspace = state.workspace;
  const selectedTeam = workspace?.team;
  const activeMembers = useMemo(
    () => (workspace?.members || []).filter(member => member.status === 'active'),
    [workspace]
  );
  const teamAttributes = useMemo(() => workspace?.attributes || [], [workspace]);
  const projectStructure = useMemo(
    () => buildProjectStructure(workspace?.projects || [], workspace?.bundles || [], workspace?.activities || []),
    [workspace]
  );
  const bundleManagerGroups = useMemo(
    () =>
      groupBundlesByProject(
        (workspace?.bundles || []).filter(bundle => bundle.status !== 'archived'),
        workspace?.projects || []
      ),
    [workspace]
  );
  const isAdmin = selectedTeam?.role === 'manager';
  const mailAdminDomains = useMemo(
    () =>
      new Set(
        state.accountEmails
          .filter(identity => identity.provider === 'mail' && identity.provider_domain && identity.can_create_mail_accounts)
          .map(identity => String(identity.provider_domain).toLowerCase())
      ),
    [state.accountEmails]
  );
  const memberDraftDomain = emailDomain(memberDraft.email);
  const canProvisionDraftMail = Boolean(isAdmin && memberDraftDomain && mailAdminDomains.has(memberDraftDomain));

  useEffect(() => {
    if (routeTeamId && routeSectionParam && !routeSection) {
      navigate(teamSectionPath(routeTeamId, 'members'), { replace: true });
    }
  }, [navigate, routeTeamId, routeSection, routeSectionParam]);

  useEffect(() => {
    if (!routeTeamId && legacyQueryTeamId) {
      navigate(teamOverviewPath(legacyQueryTeamId), { replace: true });
    }
  }, [legacyQueryTeamId, navigate, routeTeamId]);

  const reloadTeams = useEffectEvent(() => {
    loadTeams(true);
  });
  // Refresh after the screen has painted, so a mount or a keep-alive reveal shows at once.
  useEffect(() => afterNextPaint(() => reloadTeams()), [requestedTeamId]);

  useEffect(() => {
    setCollapsedSections(previous => {
      const next = openSectionsForRoute(previous, routeSection);
      // Unchanged disclosure state (for example on a keep-alive reveal) must not re-render the page.
      const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
      return [...keys].every(key => previous[key] === next[key]) ? previous : next;
    });
  }, [routeSection]);

  useEffect(() => {
    const teamId = state.selectedTeamId;
    if (teamId) return afterNextPaint(() => loadWorkspace(teamId));
  }, [state.selectedTeamId]);

  // Reset the governance form only when another team is selected: a workspace reload of the same
  // team must not discard unsaved governance edits.
  const resetGovernanceDraft = useEffectEvent(() => {
    if (!selectedTeam) return;
    const policy = parseConnectionPolicy(selectedTeam.connection_policy);
    setTeamGovernanceDraft({
      visibility: selectedTeam.visibility || 'invite_only',
      parent_team_id: selectedTeam.parent_team_id || 'none',
      connection_mode: policy.mode || 'open',
      allowed_connection_domains: (policy.allowed_domains || []).join(', '),
    });
  });
  const selectedTeamId = selectedTeam?.id;
  // The team the draft was last reset for; a keep-alive reveal re-runs this effect for the same team.
  const governanceDraftTeam = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedTeamId || governanceDraftTeam.current === selectedTeamId) return;
    governanceDraftTeam.current = selectedTeamId;
    resetGovernanceDraft();
  }, [selectedTeamId]);

  /** Load teams, emails and discovery. A `quiet` refresh of teams already shown does not re-render them as loading. */
  async function loadTeams(quiet = false) {
    setState(previous =>
      previous.loading || (quiet && previous.teams.length) ? previous : { ...previous, loading: true }
    );
    const [response, emailsResponse, discoverResponse] = await Promise.all([
      api.teams.list(),
      api.user.getEmails(),
      api.teams.discover(),
    ]);
    if (!response.success) {
      setState(previous => ({
        ...previous,
        loading: false,
        status: response.error?.message || 'Could not load teams.',
      }));
      return;
    }
    const teams = response.data || [];
    const accountEmails = emailsResponse.success ? emailsResponse.data?.emails || [] : [];
    const discoverableTeams = discoverResponse.success ? discoverResponse.data || [] : [];
    const requestedTeam = requestedTeamId ? teams.find(team => team.id === requestedTeamId) : undefined;
    setState(previous => ({
      ...previous,
      teams,
      accountEmails,
      discoverableTeams,
      selectedTeamId:
        requestedTeam?.id ||
        (previous.selectedTeamId && teams.some(team => team.id === previous.selectedTeamId)
          ? previous.selectedTeamId
          : teams[0]?.id || ''),
      loading: false,
      status: teams.length ? '' : 'Create a team workspace to start assigning shared activities.',
    }));
  }

  async function loadWorkspace(teamId: string) {
    setState(previous =>
      previous.loading || previous.workspace?.team?.id === teamId ? previous : { ...previous, loading: true }
    );
    const response = await api.teams.get(teamId);
    setState(previous => ({
      ...previous,
      workspace: response.success ? (response.data ?? null) : previous.workspace,
      loading: false,
      status: response.success ? '' : response.error?.message || 'Could not load team workspace.',
    }));
  }

  async function createTeam() {
    if (!teamDraft.name.trim()) return setStatus('Team name is required.');
    const response = await api.teams.create({
      ...teamDraft,
      visibility: teamGovernanceDraft.visibility,
      parent_team_id: teamGovernanceDraft.parent_team_id === 'none' ? null : teamGovernanceDraft.parent_team_id,
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create team.');
    setTeamDraft({ name: '', domain: '', purpose: '', membership_email: state.accountEmails[0]?.email || '' });
    await loadTeams();
    const team = response.data?.team;
    if (team?.id) selectTeam(team.id);
  }

  async function linkEmail() {
    if (!emailDraft.email.trim()) return setStatus('Email is required.');
    const response = await api.user.linkEmail({ email: emailDraft.email });
    if (!response.success) return setStatus(response.error?.message || 'Could not start email verification.');
    setEmailDraft({ email: '' });
    const verificationUrl = response.data?.verification_url;
    setStatus(
      verificationUrl
        ? `Verification started. Open the verification link from your email.`
        : response.data?.message || 'Verification started.'
    );
    await loadTeams();
  }

  async function requestJoin(team: TeamDiscoveryResult) {
    const email = team.matched_email || state.accountEmails[0]?.email;
    if (!email) return setStatus('Link an email before requesting to join a team.');
    const response = await api.teams.requestJoin(team.id, { email });
    if (!response.success) return setStatus(response.error?.message || 'Could not request access.');
    setStatus(response.data?.auto_approved ? 'Joined team.' : 'Join request sent.');
    await loadTeams();
    await loadWorkspaceIfSelected(team.id);
  }

  async function acceptInvite(team: TeamDiscoveryResult) {
    const email = team.invite_email || team.matched_email || state.accountEmails[0]?.email;
    if (!email) return setStatus('Link the invited email before accepting this team invite.');
    const response = await api.teams.acceptInvite(team.id, { email });
    if (!response.success) return setStatus(response.error?.message || 'Could not accept invite.');
    setStatus('Team invite accepted.');
    await loadTeams();
    selectTeam(team.id);
  }

  async function loadWorkspaceIfSelected(teamId: string) {
    if (state.selectedTeamId === teamId) await loadWorkspace(teamId);
  }

  async function updateTeamPolicy(sync_policy: string) {
    if (!selectedTeam?.id) return;
    const response = await api.teams.update(selectedTeam.id, { sync_policy });
    if (!response.success) return setStatus(response.error?.message || 'Could not update sync policy.');
    await loadWorkspace(selectedTeam.id);
  }

  async function updateTeamGovernance() {
    if (!selectedTeam?.id) return;
    const response = await api.teams.update(selectedTeam.id, {
      visibility: teamGovernanceDraft.visibility,
      parent_team_id: teamGovernanceDraft.parent_team_id === 'none' ? null : teamGovernanceDraft.parent_team_id,
      connection_policy: {
        mode: teamGovernanceDraft.connection_mode,
        allowed_domains: parsePolicyList(teamGovernanceDraft.allowed_connection_domains),
      },
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not update team governance.');
    setStatus('Team governance updated.');
    await Promise.all([loadTeams(), loadWorkspace(selectedTeam.id)]);
  }

  async function inviteMember() {
    if (!selectedTeam?.id || !memberDraft.email.trim()) return setStatus('Member email is required.');
    const payload = {
      email: memberDraft.email,
      display_name: memberDraft.display_name,
      role: memberDraft.role,
      ...(canProvisionDraftMail
        ? {
            mail_account: {
              enabled: Boolean(memberDraft.create_mail_account),
              role: memberDraft.mail_role,
              mailbox_address: memberDraft.email,
            },
          }
        : {}),
    };
    const response = isAdmin
      ? await api.teams.inviteMember(selectedTeam.id, payload)
      : await api.teams.requestMemberInvite(selectedTeam.id, payload);
    if (!response.success) return setStatus(response.error?.message || 'Could not add member.');
    // Only a direct invitation (administrators) returns delivery and provisioning outcomes.
    const invited = response.data && 'invite_email' in response.data ? response.data : undefined;
    const inviteEmail = invited?.invite_email;
    const mailProvisioning = invited?.mail_provisioning;
    setMemberDraft({
      email: '',
      display_name: '',
      role: 'member',
      create_mail_account: true,
      mail_role: 'user',
    });
    setStatus(
      isAdmin
        ? [inviteEmail?.sent ? 'Invite sent.' : inviteEmail?.message || 'Member invited, but email delivery was not confirmed.', mailProvisioningStatus(mailProvisioning)]
            .filter(Boolean)
            .join(' ')
        : 'Invite request sent to Fluxability administrators.'
    );
    await loadWorkspace(selectedTeam.id);
  }

  async function updateMemberRole(memberId: string, role: string) {
    if (!selectedTeam?.id) return;
    const response = await api.teams.updateMember(selectedTeam.id, memberId, { role, status: 'active' });
    if (!response.success) return setStatus(response.error?.message || 'Could not update member role.');
    setStatus(role === 'manager' ? 'Promoted to Fluxability administrator.' : 'Member role updated.');
    await loadWorkspace(selectedTeam.id);
  }

  async function decideInviteRequest(requestId: string, status: 'approved' | 'rejected') {
    if (!selectedTeam?.id) return;
    const response = await api.teams.decideMemberInviteRequest(selectedTeam.id, requestId, { status });
    if (!response.success) return setStatus(response.error?.message || 'Could not update invite request.');
    const mailProvisioning = response.data?.invite_email?.mail_provisioning;
    setStatus(
      status === 'approved'
        ? ['Invite approved and sent.', mailProvisioningStatus(mailProvisioning)].filter(Boolean).join(' ')
        : 'Invite request rejected.'
    );
    await loadWorkspace(selectedTeam.id);
  }

  async function createJoinRule() {
    if (!selectedTeam?.id || !joinRuleDraft.pattern.trim()) return setStatus('Join rule pattern is required.');
    const response = await api.teams.createJoinRule(selectedTeam.id, {
      pattern: joinRuleDraft.pattern,
      role: joinRuleDraft.role,
      auto_approve: joinRuleDraft.auto_approve,
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create join rule.');
    setJoinRuleDraft({ pattern: '', role: 'member', auto_approve: false });
    await loadWorkspace(selectedTeam.id);
  }

  async function deleteJoinRule(ruleId: string) {
    if (!selectedTeam?.id) return;
    const response = await api.teams.deleteJoinRule(selectedTeam.id, ruleId);
    if (!response.success) return setStatus(response.error?.message || 'Could not remove join rule.');
    await loadWorkspace(selectedTeam.id);
  }

  async function decideJoinRequest(requestId: string, status: 'approved' | 'rejected') {
    if (!selectedTeam?.id) return;
    const response = await api.teams.updateJoinRequest(selectedTeam.id, requestId, { status });
    if (!response.success) return setStatus(response.error?.message || 'Could not update join request.');
    await Promise.all([loadTeams(), loadWorkspace(selectedTeam.id)]);
  }

  async function createProject() {
    if (!selectedTeam?.id || !projectDraft.name.trim()) return setStatus('Project name is required.');
    const response = await api.teams.createProject(selectedTeam.id, {
      ...projectDraft,
      priority_score: Number(projectDraft.priority_score),
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create project.');
    setProjectDraft({ name: '', purpose: '', priority_score: '5' });
    await loadWorkspace(selectedTeam.id);
  }

  function startEditingProject(project: WorkspaceProject) {
    setEditingProjectId(project.id);
    setEditingProjectDraft({
      name: project.name || '',
      purpose: project.purpose || '',
      priority_score: String(project.priority_score ?? 5),
    });
  }

  function cancelEditingProject() {
    setEditingProjectId('');
    setEditingProjectDraft({ name: '', purpose: '', priority_score: '5' });
  }

  async function saveProject(projectId: string) {
    if (!selectedTeam?.id) return;
    if (!editingProjectDraft.name.trim()) return setStatus('Project name is required.');
    const response = await api.teams.updateProject(selectedTeam.id, projectId, {
      ...editingProjectDraft,
      priority_score: Number(editingProjectDraft.priority_score),
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not update project.');
    cancelEditingProject();
    await loadWorkspace(selectedTeam.id);
  }

  async function deleteProject(project: WorkspaceProject) {
    if (!selectedTeam?.id) return;
    const bundleCount = (workspace?.bundles || []).filter(bundle => bundle.project_id === project.id).length;
    const confirmed = window.confirm(
      bundleCount > 0
        ? `Delete "${project.name}"? Its ${bundleCount} bundle${bundleCount === 1 ? '' : 's'} will move to No project.`
        : `Delete "${project.name}"?`
    );
    if (!confirmed) return;
    const response = await api.teams.deleteProject(selectedTeam.id, project.id);
    if (!response.success) return setStatus(response.error?.message || 'Could not delete project.');
    if (editingProjectId === project.id) cancelEditingProject();
    await loadWorkspace(selectedTeam.id);
  }

  async function createBundle() {
    if (!selectedTeam?.id || !bundleDraft.name.trim()) return setStatus('Bundle name is required.');
    const response = await api.teams.createBundle(selectedTeam.id, {
      ...bundleDraft,
      project_id: bundleDraft.project_id === 'none' ? null : bundleDraft.project_id,
      priority_score: Number(bundleDraft.priority_score),
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create bundle.');
    setBundleDraft({ name: '', project_id: 'none', purpose: '', priority_score: '5' });
    await loadWorkspace(selectedTeam.id);
  }

  function startEditingBundle(bundle: WorkspaceBundle) {
    setEditingBundleId(bundle.id);
    setEditingBundleDraft({
      name: bundle.name || '',
      project_id: bundle.project_id || 'none',
      purpose: bundle.purpose || '',
      priority_score: String(bundle.priority_score ?? 5),
    });
  }

  function cancelEditingBundle() {
    setEditingBundleId('');
    setEditingBundleDraft({ name: '', project_id: 'none', purpose: '', priority_score: '5' });
  }

  async function saveBundle(bundleId: string) {
    if (!selectedTeam?.id) return;
    if (!editingBundleDraft.name.trim()) return setStatus('Bundle name is required.');
    const response = await api.teams.updateBundle(selectedTeam.id, bundleId, {
      ...editingBundleDraft,
      project_id: editingBundleDraft.project_id === 'none' ? null : editingBundleDraft.project_id,
      priority_score: Number(editingBundleDraft.priority_score),
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not update bundle.');
    cancelEditingBundle();
    await loadWorkspace(selectedTeam.id);
  }

  async function deleteBundle(bundle: WorkspaceBundle) {
    if (!selectedTeam?.id) return;
    const activityCount = (workspace?.activities || []).filter(activity => activity.bundle_id === bundle.id).length;
    const confirmed = window.confirm(
      activityCount > 0
        ? `This bundle has ${activityCount} activit${activityCount === 1 ? 'y' : 'ies'}. Move them before deleting "${bundle.name}".`
        : `Delete "${bundle.name}"?`
    );
    if (!confirmed) return;
    const response = await api.teams.deleteBundle(selectedTeam.id, bundle.id);
    if (!response.success) return setStatus(response.error?.message || 'Could not delete bundle.');
    if (editingBundleId === bundle.id) cancelEditingBundle();
    await loadWorkspace(selectedTeam.id);
  }

  async function createActivity() {
    if (!selectedTeam?.id) return;
    if (!activityDraft.title.trim()) return setStatus('Activity title is required.');
    if (!activityDraft.bundle_id) return setStatus('Choose a bundle before assigning an activity.');
    if (!activityDraft.assigned_to_user_id) return setStatus('Choose an assignee.');
    const missingTeamFields = teamAttributes
      .filter(attribute => Boolean(attribute.required))
      .filter(attribute => !String(activityAttributeValues[attribute.attr_id] ?? '').trim())
      .map(attribute => attribute.name);
    if (missingTeamFields.length > 0) {
      return setStatus(`Fill required team field${missingTeamFields.length === 1 ? '' : 's'}: ${missingTeamFields.join(', ')}.`);
    }
    const customAttributes = buildTeamActivityAttributes(teamAttributes, activityAttributeValues);
    const response = await api.teams.createActivity(selectedTeam.id, {
      ...activityDraft,
      duration_minutes: activityDraft.assignee_decides_duration ? undefined : Number(activityDraft.duration_minutes),
      custom_attributes: {
        ...customAttributes,
        ...(activityDraft.assignee_decides_duration ? { assignee_decides_duration: true } : {}),
      },
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create activity.');
    setActivityDraft({
      title: '',
      description: '',
      assigned_to_user_id: activityDraft.assigned_to_user_id,
      bundle_id: activityDraft.bundle_id,
      due_date: '',
      duration_minutes: '45',
      priority: 'medium',
      assignee_decides_duration: false,
    });
    setActivityAttributeValues({});
    setStatus('Activity assigned into the member calendar.');
    await loadWorkspace(selectedTeam.id);
  }

  async function createTeamAttribute() {
    if (!selectedTeam?.id) return;
    if (!attributeDraft.name.trim()) return setStatus('Team field name is required.');
    const response = await api.teams.createAttribute(selectedTeam.id, {
      ...attributeDraft,
      specificity: Number(attributeDraft.specificity),
      field_weight: Number(attributeDraft.field_weight),
      options: attributeDraft.options,
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create team field.');
    setAttributeDraft({
      name: '',
      data_type: 'float',
      specificity: '5',
      field_weight: '1',
      required: false,
      options: '',
    });
    setStatus('Team field created.');
    await loadWorkspace(selectedTeam.id);
  }

  async function deleteTeamAttribute(attributeId: string) {
    if (!selectedTeam?.id) return;
    const response = await api.teams.deleteAttribute(selectedTeam.id, attributeId);
    if (!response.success) return setStatus(response.error?.message || 'Could not delete team field.');
    setActivityAttributeValues(current => {
      const next = { ...current };
      delete next[attributeId];
      return next;
    });
    await loadWorkspace(selectedTeam.id);
  }

  async function createEmailRule() {
    if (!selectedTeam?.id || !ruleDraft.pattern.trim()) return setStatus('Rule pattern is required.');
    const response = await api.teams.createEmailRule(selectedTeam.id, {
      ...ruleDraft,
      project_id: ruleDraft.project_id === 'none' ? null : ruleDraft.project_id,
      bundle_id: ruleDraft.bundle_id === 'none' ? null : ruleDraft.bundle_id,
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create email rule.');
    setRuleDraft({
      rule_type: 'keyword',
      pattern: '',
      action: 'propose_activity',
      project_id: 'none',
      bundle_id: 'none',
    });
    await loadWorkspace(selectedTeam.id);
  }

  async function deleteEmailRule(ruleId: string) {
    if (!selectedTeam?.id) return;
    const response = await api.teams.deleteEmailRule(selectedTeam.id, ruleId);
    if (!response.success) return setStatus(response.error?.message || 'Could not delete email rule.');
    await loadWorkspace(selectedTeam.id);
  }

  async function createFeed() {
    if (!selectedTeam?.id) return;
    const response = await api.teams.createFeed(selectedTeam.id, {
      permission: feedDraft.permission,
      label: feedDraft.label,
      include_gcal: feedDraft.include_gcal,
      gcal_visibility: feedDraft.gcal_visibility,
      metadata: {
        calendar_ids: parseCalendarIds(feedDraft.calendar_ids),
      },
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create calendar feed.');
    const url = response.data?.feed?.url || '';
    setLatestFeedUrl(url);
    setFeedDraft({
      permission: 'busy_free',
      label: '',
      include_gcal: false,
      gcal_visibility: 'busy_free',
      calendar_ids: '',
    });
    await loadWorkspace(selectedTeam.id);
  }

  async function revokeFeed(feedId: string) {
    if (!selectedTeam?.id) return;
    const response = await api.teams.revokeFeed(selectedTeam.id, feedId);
    if (!response.success) return setStatus(response.error?.message || 'Could not revoke calendar feed.');
    setStatus('Calendar feed revoked.');
    await loadWorkspace(selectedTeam.id);
  }

  async function createConnection() {
    if (!selectedTeam?.id) return;
    const response = await api.teams.createConnection(selectedTeam.id, {
      provider: connectionDraft.provider,
      remote_domain: connectionDraft.remote_domain,
      direction: connectionDraft.direction,
      metadata: {
        remote_account_email: connectionDraft.remote_account_email,
        team_visibility: connectionDraft.team_visibility,
        share_gcal: connectionDraft.share_gcal,
        gcal_visibility: connectionDraft.gcal_visibility,
        calendar_ids: parseCalendarIds(connectionDraft.calendar_ids),
      },
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not create connection.');
    setConnectionDraft({
      provider: 'flexiplanner',
      remote_domain: '',
      direction: 'one_way_in',
      remote_account_email: '',
      team_visibility: 'team_names',
      share_gcal: true,
      gcal_visibility: 'busy_free',
      calendar_ids: '',
    });
    await loadWorkspace(selectedTeam.id);
  }

  async function deleteConnection(connectionId: string) {
    if (!selectedTeam?.id) return;
    const response = await api.teams.deleteConnection(selectedTeam.id, connectionId);
    if (!response.success) return setStatus(response.error?.message || 'Could not delete integration.');
    setStatus('Integration deleted.');
    await loadWorkspace(selectedTeam.id);
  }

  async function provisionMemberMailAccount(member: TeamMember, role: 'user' | 'admin') {
    if (!selectedTeam?.id || !member?.id) return;
    const response = await api.teams.provisionMemberMailAccount(selectedTeam.id, member.id, {
      role,
      mailbox_address: member.email,
    });
    if (!response.success) return setStatus(response.error?.message || 'Could not provision Mail account.');
    setStatus(mailProvisioningStatus(response.data?.mail_provisioning) || 'Mail account checked.');
    await loadWorkspace(selectedTeam.id);
  }

  function selectTeam(teamId: string) {
    setState(previous => ({ ...previous, selectedTeamId: teamId }));
    navigate(routeSection ? teamSectionPath(teamId, routeSection) : teamOverviewPath(teamId));
  }

  function goToSection(section: TeamSectionKey) {
    const teamId = selectedTeam?.id || state.selectedTeamId;
    if (!teamId) return;
    setCollapsedSections(previous => openSectionsForRoute(previous, section));
    navigate(teamSectionPath(teamId, section));
  }

  function toggleSection(section: string) {
    setCollapsedSections(previous => ({ ...previous, [section]: !previous[section] }));
  }

  function exportTeamReport() {
    if (!workspace || !selectedTeam) return;
    const now = new Date().toLocaleString();
    const activities = workspace.activities || [];
    const members = workspace.members || [];
    const projects = workspace.projects || [];
    const bundles = workspace.bundles || [];
    const emptyRow = (message: string, columns: number) =>
      `<tr><td colspan="${columns}" class="empty">${escapeHtml(message)}</td></tr>`;
    const memberRows = members.length
      ? members
          .map(
            member =>
              `<tr><td>${escapeHtml(member.display_name || '')}</td><td>${escapeHtml(member.email || '')}</td><td>${escapeHtml(roleLabel(member.role))}</td><td>${escapeHtml(member.status || '')}</td></tr>`
          )
          .join('')
      : emptyRow('No members yet.', 4);
    const projectRows = projects.length
      ? projects
          .map(
            project =>
              `<tr><td>${escapeHtml(project.name || '')}</td><td>${escapeHtml(String(project.priority_score ?? ''))}</td><td>${escapeHtml(project.purpose || '')}</td></tr>`
          )
          .join('')
      : emptyRow('No projects yet.', 3);
    const bundleRows = bundles.length
      ? bundles
          .map(
            bundle =>
              `<tr><td>${escapeHtml(bundle.name || '')}</td><td>${escapeHtml(projectName(projects, bundle.project_id) || 'No project')}</td><td>${escapeHtml(String(bundle.priority_score ?? ''))}</td></tr>`
          )
          .join('')
      : emptyRow('No bundles yet.', 3);
    const activityRows = activities.length
      ? activities
          .map(activity => {
            const title = activity.title || activity.core_fields?.title || 'Untitled activity';
            const assignee =
              activity.assigned_to_name ||
              memberName(members, activity.assigned_to_user_id || activity.user_id) ||
              activity.assigned_to_email ||
              'Unassigned';
            const duration =
              activity.duration_minutes ??
              activity.core_fields?.duration_minutes ??
              '';
            const priority = activity.priority || activity.core_fields?.priority || '';
            const dueDate = activity.due_date || activity.core_fields?.due_date || '';
            return `<tr><td>${escapeHtml(title)}</td><td>${escapeHtml(assignee)}</td><td>${escapeHtml(bundleName(bundles, activity.bundle_id) || activity.bundle_name || '')}</td><td>${escapeHtml(formatDateTime(dueDate) || 'No due date')}</td><td>${escapeHtml(duration ? `${duration}m` : '')}</td><td>${escapeHtml(priority)}</td></tr>`;
          })
          .join('')
      : emptyRow('No assigned activities yet.', 6);
    const reportTitle = `${selectedTeam.name} Fluxability report`;
    const html = `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${escapeHtml(reportTitle)}</title>
          <style>
            @page { margin: 14mm; }
            * { box-sizing: border-box; }
            body {
              font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              color: #071015;
              background: #f8fafc;
              margin: 0;
              padding: 24px;
            }
            .toolbar {
              align-items: center;
              background: #071015;
              border-radius: 10px;
              color: white;
              display: flex;
              gap: 12px;
              justify-content: space-between;
              margin: 0 auto 18px;
              max-width: 980px;
              padding: 12px 14px;
            }
            .toolbar button {
              background: #2dd4bf;
              border: 0;
              border-radius: 8px;
              color: #042f2e;
              cursor: pointer;
              font-weight: 700;
              padding: 8px 12px;
            }
            main {
              background: white;
              border: 1px solid #dbeafe;
              border-radius: 12px;
              box-shadow: 0 18px 60px rgba(15, 23, 42, .10);
              margin: 0 auto;
              max-width: 980px;
              padding: 32px;
            }
            h1 { margin: 0 0 4px; font-size: 28px; line-height: 1.1; }
            h2 { margin: 28px 0 10px; font-size: 13px; text-transform: uppercase; letter-spacing: .08em; color: #0f766e; }
            .meta { color: #64748b; font-size: 13px; }
            .summary { display: grid; gap: 8px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 18px; }
            .summary div { border: 1px solid #ccfbf1; border-radius: 8px; padding: 10px; }
            .summary strong { display: block; font-size: 18px; }
            .summary span { color: #64748b; font-size: 11px; text-transform: uppercase; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            th, td { border: 1px solid #dbeafe; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #ecfeff; color: #164e63; }
            .empty { color: #64748b; font-style: italic; text-align: center; }
            @media print {
              body { background: white; padding: 0; }
              .toolbar { display: none; }
              main { border: 0; border-radius: 0; box-shadow: none; max-width: none; padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <span>Choose “Save as PDF” in the print dialog.</span>
            <button type="button" onclick="window.print()">Print / Save PDF</button>
          </div>
          <main>
            <h1>${escapeHtml(selectedTeam.name)}</h1>
            <div class="meta">Fluxability team report · ${escapeHtml(now)}</div>
            <section class="summary">
              <div><strong>${members.length}</strong><span>Members</span></div>
              <div><strong>${projects.length}</strong><span>Projects</span></div>
              <div><strong>${bundles.length}</strong><span>Bundles</span></div>
              <div><strong>${activities.length}</strong><span>Activities</span></div>
            </section>
            <h2>Members</h2>
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr></thead>
              <tbody>${memberRows}</tbody>
            </table>
            <h2>Projects</h2>
            <table>
              <thead><tr><th>Project</th><th>Priority</th><th>Purpose</th></tr></thead>
              <tbody>${projectRows}</tbody>
            </table>
            <h2>Bundles</h2>
            <table>
              <thead><tr><th>Bundle</th><th>Project</th><th>Priority</th></tr></thead>
              <tbody>${bundleRows}</tbody>
            </table>
            <h2>Assigned Activities</h2>
            <table>
              <thead><tr><th>Activity</th><th>Assignee</th><th>Bundle</th><th>Due</th><th>Duration</th><th>Priority</th></tr></thead>
              <tbody>${activityRows}</tbody>
            </table>
          </main>
          <script>
            window.addEventListener('load', () => {
              document.title = ${JSON.stringify(reportTitle)};
              setTimeout(() => window.print(), 350);
            });
          </script>
        </body>
      </html>`;
    const report = window.open('', '_blank');
    if (!report) return setStatus('Allow popups to export the team report.');
    report.document.open();
    report.document.write(html);
    report.document.close();
    report.focus();
    setStatus('Report opened. Choose Save as PDF in the print dialog.');
  }

  function setStatus(status: string) {
    setState(previous => ({ ...previous, status }));
  }

  return (
    <div className="min-h-full overflow-x-hidden bg-background px-2 py-2 text-foreground sm:px-4 sm:py-4 lg:px-6">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:gap-4">
        <section className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Badge variant="outline" className="mb-1 rounded-md">
              Team mode
            </Badge>
            <h2 className="text-xl font-semibold tracking-tight sm:text-3xl">Fluxability Teams</h2>
            <p className="max-w-2xl text-xs text-muted-foreground sm:text-sm">
              Shared workspaces assign activities into personal calendars while keeping private activity
              details outside the team boundary.
            </p>
          </div>
          <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => loadTeams()} disabled={state.loading}>
            <RefreshCw className={cn('mr-2 h-4 w-4', state.loading && 'animate-spin')} />
            Refresh
          </Button>
        </section>

        {state.status && (
          <div className="rounded-md border border-primary/20 bg-primary/10 px-3 py-2 text-sm">
            {state.status}
          </div>
        )}

        <div className="grid min-w-0 gap-3 lg:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)]">
          <div className="min-w-0 space-y-3">
            <CollapsibleSection
              id="team-create"
              title="Create Team or Subteam"
              description="Any active member can create a subteam under a team they belong to."
              icon={Plus}
              collapsed={Boolean(collapsedSections.createTeam)}
              onToggle={() => toggleSection('createTeam')}
            >
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="teamName">Team name</Label>
                  <Input
                    id="teamName"
                    placeholder="Team name"
                    value={teamDraft.name}
                    onChange={event => setTeamDraft({ ...teamDraft, name: event.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="teamDomain">Domain</Label>
                  <Input
                    id="teamDomain"
                    placeholder="hydroterrace.com"
                    value={teamDraft.domain}
                    onChange={event => setTeamDraft({ ...teamDraft, domain: event.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="teamPurpose">Purpose</Label>
                  <Textarea
                    id="teamPurpose"
                    placeholder="Purpose"
                    value={teamDraft.purpose}
                    onChange={event => setTeamDraft({ ...teamDraft, purpose: event.target.value })}
                  />
                </div>
                <Label>Visibility</Label>
                <Select
                  value={teamGovernanceDraft.visibility}
                  onValueChange={visibility => setTeamGovernanceDraft({ ...teamGovernanceDraft, visibility })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamVisibilityOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Label>Parent team</Label>
                <Select
                  value={teamGovernanceDraft.parent_team_id}
                  onValueChange={parent_team_id =>
                    setTeamGovernanceDraft({ ...teamGovernanceDraft, parent_team_id })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Parent team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No parent team</SelectItem>
                    {state.teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>
                        Subteam of {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Label>Join as email</Label>
                <Select
                  value={teamDraft.membership_email || state.accountEmails[0]?.email || ''}
                  onValueChange={membership_email => setTeamDraft({ ...teamDraft, membership_email })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Join as email" />
                  </SelectTrigger>
                  <SelectContent>
                    {state.accountEmails.map(identity => (
                      <SelectItem key={identity.email} value={identity.email}>
                        {identity.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="w-full" onClick={createTeam}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create team
                </Button>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              id="team-account-emails"
              title="Account emails"
              description="Use one Fluxability account with every verified work identity."
              icon={AtSign}
              collapsed={Boolean(collapsedSections.accountEmails)}
              onToggle={() => toggleSection('accountEmails')}
            >
              <div className="space-y-3">
                <div className="space-y-2">
                  {state.accountEmails.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No linked emails loaded.</p>
                  ) : (
                    state.accountEmails.map(identity => (
                      <div key={identity.email} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
                        <span className="min-w-0 truncate">{identity.email}</span>
                        <Badge variant={identity.is_primary ? 'secondary' : 'outline'} className="rounded-md">
                          {identity.provider}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="link another email"
                    value={emailDraft.email}
                    onChange={event => setEmailDraft({ email: event.target.value })}
                  />
                  <Button variant="outline" onClick={linkEmail}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  className="h-auto w-full justify-start whitespace-normal px-0 text-left leading-snug"
                  onClick={() => window.location.assign('/settings')}
                >
                  <Link className="mr-2 h-4 w-4 shrink-0" />
                  <span className="min-w-0">Manage Mail, Zoho, GitHub, and pending verification</span>
                </Button>
              </div>
            </CollapsibleSection>

            <Card className="rounded-md">
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-base">Workspaces</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 p-3 pt-0 sm:p-4 sm:pt-0">
                {state.teams.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No teams yet.</p>
                ) : (
                  state.teams.map(team => (
                    <button
                      key={team.id}
                      type="button"
                      className={cn(
                        'flex w-full min-w-0 items-center justify-between rounded-md border px-2.5 py-2 text-left text-sm transition-colors',
                        state.selectedTeamId === team.id
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      )}
                      onClick={() => selectTeam(team.id)}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{team.name}</span>
                        <span className="block truncate text-xs opacity-70">{team.domain || team.slug}</span>
                      </span>
                      <Badge variant="secondary" className="ml-2 rounded-md">
                        {roleLabel(team.role)}
                      </Badge>
                    </button>
                  ))
                )}
              </CardContent>
            </Card>

            <CollapsibleSection
              id="team-join"
              title="Join teams"
              icon={UserPlus}
              collapsed={Boolean(collapsedSections.joinTeams)}
              onToggle={() => toggleSection('joinTeams')}
            >
              <div className="space-y-2">
                {state.discoverableTeams.filter(team => team.member_status !== 'active').length === 0 ? (
                  <p className="text-sm text-muted-foreground">No joinable teams for your linked emails yet.</p>
                ) : (
                  state.discoverableTeams
                    .filter(team => team.member_status !== 'active')
                    .map(team => (
                      <div key={team.id} className="rounded-md border p-3 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 truncate font-medium">
                              {visibilityIcon(team.visibility)}
                              <span className="truncate">{team.name}</span>
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {team.domain || team.slug} · {visibilityLabel(team.visibility)}
                            </p>
                          </div>
                          {team.can_accept_invite ? (
                            <Button size="sm" onClick={() => acceptInvite(team)}>
                              Accept
                            </Button>
                          ) : team.request_status ? (
                            <Badge variant="outline" className="rounded-md">
                              {team.request_status}
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => requestJoin(team)}
                              disabled={!team.can_join && !team.can_request}
                            >
                              {team.can_join ? 'Join' : 'Request'}
                            </Button>
                          )}
                        </div>
                        {team.matched_email && (
                          <p className="mt-2 truncate text-xs text-muted-foreground">Using {team.matched_email}</p>
                        )}
                        {team.can_accept_invite && (
                          <p className="mt-1 text-xs text-muted-foreground">You were invited to this workspace.</p>
                        )}
                      </div>
                    ))
                )}
              </div>
            </CollapsibleSection>
          </div>

          <div className="min-w-0 space-y-3 sm:space-y-4">
            {!workspace || !selectedTeam ? (
              <Card className="rounded-md">
                <CardHeader className="p-3 sm:p-4">
                  <CardTitle>Select or create a team</CardTitle>
                  <CardDescription>Team controls appear here once a workspace is loaded.</CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <>
                <Card className="rounded-md border-primary/15 bg-card">
                  <CardHeader className="p-3 sm:p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                          <UsersRound className="h-5 w-5 text-primary" />
                          <TeamBrandIdentity teamId={selectedTeam.id} name={selectedTeam.name} />
                        </CardTitle>
                        <CardDescription>{selectedTeam.purpose || selectedTeam.domain || selectedTeam.slug}</CardDescription>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <Badge variant="outline" className="rounded-md">
                            {visibilityIcon(selectedTeam.visibility)}
                            <span className="ml-1">{visibilityLabel(selectedTeam.visibility)}</span>
                          </Badge>
                          {selectedTeam.parent_team_id && (
                            <Badge variant="secondary" className="rounded-md">
                              Sub-team
                            </Badge>
                          )}
                          {isAdmin && (
                            <Badge variant="secondary" className="rounded-md">
                              <ShieldCheck className="mr-1 h-3 w-3" />
                              Fluxability administrator
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex w-full flex-col gap-2 sm:w-56 sm:shrink-0">
                        <Button variant="outline" size="sm" onClick={exportTeamReport}>
                          <Copy className="mr-2 h-4 w-4" />
                          Export PDF
                        </Button>
                        <Label>Sync policy</Label>
                        <Select
                          value={selectedTeam.sync_policy}
                          onValueChange={updateTeamPolicy}
                          disabled={!isAdmin}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {syncPolicies.map(policy => (
                              <SelectItem key={policy.value} value={policy.value}>
                                {policy.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
	                  </CardHeader>
	                </Card>

                <div className="flex max-w-full gap-1 overflow-x-auto rounded-md border bg-card p-1.5">
                  <RouterLink
                    to={teamOverviewPath(selectedTeam.id)}
                    className={cn(
                      'shrink-0 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors sm:px-3 sm:py-2 sm:text-sm',
                      !routeSection ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                    )}
                  >
                    Overview
                  </RouterLink>
                  {teamSections.map(section => (
                    <RouterLink
                      key={section.id}
                      to={teamSectionPath(selectedTeam.id, section.id)}
                      className={cn(
                        'shrink-0 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors sm:px-3 sm:py-2 sm:text-sm',
                        routeSection === section.id ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                      )}
                      onClick={() =>
                        setCollapsedSections(previous => openSectionsForRoute(previous, section.id))
                      }
                    >
                      {section.label}
                    </RouterLink>
                  ))}
                </div>

                {visitedExtended.map(section => (
                  <RetainedSection key={section} mode={section === routeSection ? 'visible' : 'hidden'}>
                    <Suspense fallback={<p role="status">Opening team page…</p>}><TeamExtendedSection key={`${selectedTeam.id}:${section}`} section={section} teamId={selectedTeam.id} workspace={workspace} isAdmin={isAdmin} /></Suspense>
                  </RetainedSection>
                ))}
                {visitedSections.includes('overview') && (<RetainedSection mode={!routeSection ? 'visible' : 'hidden'}>
                  <>
                <TeamOverview workspace={workspace} />
                <Card className="rounded-md">
                  <CardHeader className="p-3 sm:p-4">
                    <CardTitle className="text-base">Team pages</CardTitle>
                    <CardDescription>Open a dedicated page for this team workspace.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-2 p-3 pt-0 sm:grid-cols-2 sm:p-4 sm:pt-0 xl:grid-cols-3">
                    {teamSections.map(section => {
                      const Icon = section.icon;
                      return (
                        <button
                          key={section.id}
                          type="button"
                          className="flex min-w-0 items-start gap-3 rounded-md border bg-muted/10 p-3 text-left text-sm transition-colors hover:bg-accent"
                          onClick={() => goToSection(section.id)}
                        >
                          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span className="min-w-0">
                            <span className="block truncate font-medium">{section.label}</span>
                            <span className="mt-1 block text-xs text-muted-foreground">{section.description}</span>
                          </span>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>

                <CollapsibleSection
                  id="team-overview"
                  title="Workspace signals"
                  description="Quick counts for subscription calendars and linked Fluxability instances."
                  icon={Cloud}
                  collapsed={Boolean(collapsedSections.overview)}
                  onToggle={() => toggleSection('overview')}
                >
                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="rounded-md border border-primary/20 bg-primary/10 p-3 text-sm">
                      <div className="mb-1 flex items-center gap-2 font-medium">
                        <CalendarDays className="h-4 w-4" />
                        Calendar integrations
                      </div>
                      <p className="text-muted-foreground">
                        {(workspace.feeds || []).length} active feed{(workspace.feeds || []).length === 1 ? '' : 's'} for this account.
                      </p>
                    </div>
                    <div className="rounded-md border bg-muted/20 p-3 text-sm">
                      <div className="mb-1 flex items-center gap-2 font-medium">
                        <Cloud className="h-4 w-4" />
                        Other Fluxability accounts
                      </div>
                      <p className="text-muted-foreground">
                        {(workspace.connections || []).length} active integration{(workspace.connections || []).length === 1 ? '' : 's'}.
                      </p>
                    </div>
                  </div>
                </CollapsibleSection>

                  </>
                </RetainedSection>)}

                {visitedSections.includes('access') && (<RetainedSection mode={routeSection === 'access' ? 'visible' : 'hidden'}>
                <CollapsibleSection
                  id="team-governance"
                  title="Team visibility and linking"
                  description="Fluxability administrators control who can discover this team and which domains can connect accounts."
                  icon={Globe2}
                  collapsed={Boolean(collapsedSections.governance)}
                  onToggle={() => toggleSection('governance')}
                >
                  <div className="space-y-3">
                    <div className="grid gap-2 md:grid-cols-[180px_1fr_180px_auto]">
                      <Select
                        value={teamGovernanceDraft.visibility}
                        onValueChange={visibility => setTeamGovernanceDraft({ ...teamGovernanceDraft, visibility })}
                        disabled={!isAdmin}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {teamVisibilityOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={teamGovernanceDraft.parent_team_id}
                        onValueChange={parent_team_id =>
                          setTeamGovernanceDraft({ ...teamGovernanceDraft, parent_team_id })
                        }
                        disabled={!isAdmin}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No parent team</SelectItem>
                          {state.teams
                            .filter(team => team.id !== selectedTeam.id)
                            .map(team => (
                              <SelectItem key={team.id} value={team.id}>
                                {team.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={teamGovernanceDraft.connection_mode}
                        onValueChange={connection_mode =>
                          setTeamGovernanceDraft({ ...teamGovernanceDraft, connection_mode })
                        }
                        disabled={!isAdmin}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Any Fluxability domain</SelectItem>
                          <SelectItem value="restricted">Approved domains only</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={updateTeamGovernance} disabled={!isAdmin}>
                        Save
                      </Button>
                    </div>
                    <Textarea
                      className="min-h-16"
                      placeholder="Approved domains, comma or line separated. Wildcards like *.hydroterrace.com are supported."
                      value={teamGovernanceDraft.allowed_connection_domains}
                      onChange={event =>
                        setTeamGovernanceDraft({
                          ...teamGovernanceDraft,
                          allowed_connection_domains: event.target.value,
                        })
                      }
                      disabled={!isAdmin || teamGovernanceDraft.connection_mode !== 'restricted'}
                    />
                    <div className="grid gap-2 sm:grid-cols-3">
                      {teamVisibilityOptions.map(option => (
                        <div key={option.value} className="rounded-md border bg-muted/20 p-3 text-xs">
                          <div className="mb-1 flex items-center gap-2 font-medium">
                            {visibilityIcon(option.value)}
                            {option.label}
                          </div>
                          <p className="text-muted-foreground">{option.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CollapsibleSection>
                </RetainedSection>)}

		                <div className="grid gap-3 xl:grid-cols-2">
                  {visitedSections.includes('members') && (<RetainedSection mode={routeSection === 'members' ? 'visible' : 'hidden'}>
                  <CollapsibleSection
                    id="team-users"
                    title="Members"
                    description="Administrators invite directly; members can request an invite for approval."
                    icon={Shield}
                    collapsed={Boolean(collapsedSections.users)}
                    onToggle={() => toggleSection('users')}
                  >
                    <div className="space-y-3 sm:space-y-4">
                      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_120px_auto]">
                        <Input
                          placeholder="email"
                          value={memberDraft.email}
                          onChange={event => setMemberDraft({ ...memberDraft, email: event.target.value })}
                        />
                        <Input
                          placeholder="name"
                          value={memberDraft.display_name}
                          onChange={event => setMemberDraft({ ...memberDraft, display_name: event.target.value })}
                        />
                        <Select
                          value={memberDraft.role}
                          onValueChange={role => setMemberDraft({ ...memberDraft, role })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="manager">Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button className="w-full sm:w-auto" onClick={inviteMember} disabled={!memberDraft.email.trim()}>
                          <Mail className="mr-2 h-4 w-4" />
                          Invite
                        </Button>
                      </div>

                      {canProvisionDraftMail && (
                        <div className="rounded-md border border-primary/20 bg-primary/10 p-3 text-sm">
                          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <label className="flex items-start gap-3">
                              <Checkbox
                                checked={memberDraft.create_mail_account}
                                onCheckedChange={checked =>
                                  setMemberDraft({ ...memberDraft, create_mail_account: Boolean(checked) })
                                }
                              />
                              <span>
                                <span className="block font-medium">Create Mail app account if missing</span>
                                <span className="block text-xs text-muted-foreground">
                                  Authorized through your linked Mail admin identity for {memberDraftDomain}.
                                </span>
                              </span>
                            </label>
                            <Select
                              value={memberDraft.mail_role}
                              onValueChange={mail_role => setMemberDraft({ ...memberDraft, mail_role })}
                              disabled={!memberDraft.create_mail_account}
                            >
                              <SelectTrigger className="w-full sm:w-40">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">Mail user</SelectItem>
                                <SelectItem value="admin">Mail admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            If the Mail account is created, the invite email includes a temporary password and first-login instructions.
                          </p>
                        </div>
                      )}
                      {memberDraft.email.trim() && !canProvisionDraftMail && isAdmin && (
                        <p className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
                          Fluxability will send an invite. Link a Mail admin email for {memberDraftDomain || 'this domain'} to create Mail-app accounts from here.
                        </p>
                      )}

                      <div className="divide-y rounded-md border">
                        {(workspace.members || []).map(member => (
                          <div key={member.id} className="flex flex-col gap-3 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 items-center gap-3">
                              <UserAvatar name={member.display_name} email={member.email} avatarUrl={member.avatar_url} />
                              <div className="min-w-0">
                                <p className="truncate font-medium">{member.display_name || member.email}</p>
                                <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {isAdmin && member.status === 'active' ? (
                                <Select
                                  value={member.role}
                                  onValueChange={role => updateMemberRole(member.id, role)}
                                >
                                  <SelectTrigger className="h-8 w-full sm:w-40">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="manager">Administrator</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant="outline" className="rounded-md">
                                  {roleLabel(member.role)}
                                </Badge>
                              )}
                              <Badge variant={member.status === 'active' ? 'secondary' : 'outline'} className="rounded-md">
                                {member.status}
                              </Badge>
                              {isAdmin && mailAdminDomains.has(emailDomain(member.email)) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => provisionMemberMailAccount(member, 'admin')}
                                >
                                  <ShieldCheck className="mr-2 h-4 w-4" />
                                  Mail admin
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleSection>
                  </RetainedSection>)}

                  {visitedSections.includes('access') && (<RetainedSection mode={routeSection === 'access' ? 'visible' : 'hidden'}>
                  <CollapsibleSection
                    id="team-requests"
                    title="Join Governance"
                    description="Control who can request access and approve pending team members."
                    icon={ShieldCheck}
                    collapsed={Boolean(collapsedSections.requests)}
                    onToggle={() => toggleSection('requests')}
                  >
                    <div className="space-y-3 sm:space-y-4">
                      <div className="grid gap-2 sm:grid-cols-[1fr_120px_auto_auto]">
                        <Input
                          placeholder="*@hydroterrace.com"
                          value={joinRuleDraft.pattern}
                          disabled={!isAdmin}
                          onChange={event => setJoinRuleDraft({ ...joinRuleDraft, pattern: event.target.value })}
                        />
                        <Select
                          value={joinRuleDraft.role}
                          onValueChange={role => setJoinRuleDraft({ ...joinRuleDraft, role })}
                          disabled={!isAdmin}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
	                            <SelectItem value="member">Member</SelectItem>
	                            <SelectItem value="manager">Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                          <Checkbox
                            checked={joinRuleDraft.auto_approve}
                            disabled={!isAdmin}
                            onCheckedChange={checked =>
                              setJoinRuleDraft({ ...joinRuleDraft, auto_approve: Boolean(checked) })
                            }
                          />
                          Auto
                        </label>
                        <Button onClick={createJoinRule} disabled={!isAdmin}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="divide-y rounded-md border">
                        {(workspace.join_rules || []).length === 0 ? (
                          <p className="p-3 text-sm text-muted-foreground">No join rules yet.</p>
                        ) : (
                          (workspace.join_rules || []).map(rule => (
                            <div key={rule.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                              <div className="min-w-0">
                                <p className="truncate font-medium">{rule.pattern}</p>
                                <p className="truncate text-xs text-muted-foreground">
	                                  {roleLabel(rule.role)}
	                                  {rule.auto_approve ? ' · auto approve' : ' · administrator approval'}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteJoinRule(rule.id)}
                                disabled={!isAdmin}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>

                      <div className="divide-y rounded-md border">
                        {(workspace.join_requests || []).length === 0 ? (
                          <p className="p-3 text-sm text-muted-foreground">No pending join requests.</p>
                        ) : (
                          (workspace.join_requests || []).map(request => (
                            <div key={request.id} className="flex flex-col gap-3 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <p className="truncate font-medium">{request.display_name || request.email}</p>
                                <p className="truncate text-xs text-muted-foreground">
	                                  {request.email} · {roleLabel(request.requested_role)}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2 sm:justify-end">
                                <Button variant="outline" size="sm" onClick={() => decideJoinRequest(request.id, 'rejected')}>
                                  Reject
                                </Button>
                                <Button size="sm" onClick={() => decideJoinRequest(request.id, 'approved')}>
                                  Approve
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <div className="divide-y rounded-md border">
                        {(workspace.invite_requests || []).length === 0 ? (
                          <p className="p-3 text-sm text-muted-foreground">No pending invite requests.</p>
                        ) : (
                          (workspace.invite_requests || []).map(request => (
                            <div key={request.id} className="flex flex-col gap-3 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <p className="truncate font-medium">{request.display_name || request.email}</p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {request.email} · {roleLabel(request.requested_role)} · {request.provider_hint}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2 sm:justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => decideInviteRequest(request.id, 'rejected')}
                                >
                                  Reject
                                </Button>
                                <Button size="sm" onClick={() => decideInviteRequest(request.id, 'approved')}>
                                  Approve
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </CollapsibleSection>
                  </RetainedSection>)}

                  {visitedSections.includes('activity') && (<RetainedSection mode={routeSection === 'activity' ? 'visible' : 'hidden'}>
                  <CollapsibleSection
                    id="team-activities"
                    title="Assign Activity"
                    description="Assigned activities enter the assignee's personal scheduler."
                    icon={Activity}
                    collapsed={Boolean(collapsedSections.activities)}
                    onToggle={() => toggleSection('activities')}
                  >
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label htmlFor="teamActivityTitle">Activity title</Label>
                        <Input
                          id="teamActivityTitle"
                          placeholder="Activity title"
                          value={activityDraft.title}
                          onChange={event => setActivityDraft({ ...activityDraft, title: event.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="teamActivityDescription">Notes or source context</Label>
                        <Textarea
                          id="teamActivityDescription"
                          placeholder="Notes or source context"
                          value={activityDraft.description}
                          onChange={event =>
                            setActivityDraft({ ...activityDraft, description: event.target.value })
                          }
                        />
                      </div>
                      {isAdmin && (
                        <AssigneeSuggestions
                          teamId={selectedTeam.id}
                          title={activityDraft.title}
                          description={activityDraft.description}
                          durationMinutes={activityDraft.assignee_decides_duration ? '' : activityDraft.duration_minutes}
                          dueDate={activityDraft.due_date}
                          selectedUserId={activityDraft.assigned_to_user_id}
                          onChoose={assigned_to_user_id => setActivityDraft({ ...activityDraft, assigned_to_user_id })}
                        />
                      )}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1">
                          <Label>Assignee</Label>
                          <Select
                            value={activityDraft.assigned_to_user_id}
                            onValueChange={assigned_to_user_id =>
                              setActivityDraft({ ...activityDraft, assigned_to_user_id })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Assignee" />
                            </SelectTrigger>
                            <SelectContent>
                              {activeMembers
                                .filter((member): member is TeamMember & { /** Linked account. */ user_id: string } =>
                                  Boolean(member.user_id)
                                )
                                .map(member => (
                                  <SelectItem key={member.id} value={member.user_id}>
                                    {member.display_name || member.email}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label>Bundle</Label>
                          <Select
                            value={activityDraft.bundle_id}
                            onValueChange={bundle_id => setActivityDraft({ ...activityDraft, bundle_id })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Bundle" />
                            </SelectTrigger>
                            <SelectContent>
                              {(workspace.bundles || []).map(bundle => (
                                <SelectItem key={bundle.id} value={bundle.id}>
                                  {bundle.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="teamActivityDueDate">Due date</Label>
                          <Input
                            id="teamActivityDueDate"
                            type="datetime-local"
                            value={activityDraft.due_date}
                            onChange={event => setActivityDraft({ ...activityDraft, due_date: event.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="teamActivityDuration">Duration minutes</Label>
                          <Input
                            id="teamActivityDuration"
                            type="number"
                            min="1"
                            value={activityDraft.duration_minutes}
                            disabled={activityDraft.assignee_decides_duration}
                            onChange={event =>
                              setActivityDraft({ ...activityDraft, duration_minutes: event.target.value })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Priority</Label>
                          <Select
                            value={activityDraft.priority}
                            onValueChange={priority => setActivityDraft({ ...activityDraft, priority })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {teamAttributes.length > 0 && (
                        <div className="rounded-md border bg-muted/20 p-3">
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <div>
                              <h3 className="text-sm font-medium">Team fields</h3>
                              <p className="text-xs text-muted-foreground">
                                These values travel with the activity and feed team-level inference.
                              </p>
                            </div>
                            <Badge variant="outline" className="rounded-md">
                              {teamAttributes.filter(attribute => attribute.required).length} required
                            </Badge>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2">
                            {teamAttributes.map(attribute => (
                              <TeamAttributeInput
                                key={attribute.attr_id}
                                attribute={attribute}
                                value={activityAttributeValues[attribute.attr_id] || ''}
                                onChange={value =>
                                  setActivityAttributeValues(current => ({
                                    ...current,
                                    [attribute.attr_id]: value,
                                  }))
                                }
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      <label className="flex items-start gap-3 rounded-md border bg-muted/30 p-3 text-sm">
                        <Checkbox
                          checked={activityDraft.assignee_decides_duration}
                          onCheckedChange={checked =>
                            setActivityDraft({ ...activityDraft, assignee_decides_duration: Boolean(checked) })
                          }
                        />
                        <span>
                          <span className="block font-medium">Let assignee decide duration</span>
                          <span className="block text-xs text-muted-foreground">
                            Fluxability will still infer a starter estimate, but the assignee gets prompted to confirm it before scheduling.
                          </span>
                        </span>
                      </label>
	                      <Button onClick={createActivity}>
	                        <Plus className="mr-2 h-4 w-4" />
	                        Assign activity
	                      </Button>
                      <div className="divide-y rounded-md border">
                        {(workspace.activities || []).length === 0 ? (
                          <p className="p-3 text-sm text-muted-foreground">No assigned team activities yet.</p>
	                        ) : (
	                          (workspace.activities || []).map(activity => {
	                            const teamFieldValues = Array.isArray(activity.custom_attribute_values)
	                              ? activity.custom_attribute_values
	                              : [];
	                            return (
	                              <div key={activity.id} className="grid gap-2 p-3 text-sm md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-center">
	                                <div className="min-w-0">
	                                  <p className="truncate font-medium">{activity.title}</p>
	                                  <p className="truncate text-xs text-muted-foreground">
	                                    {activity.bundle_name || bundleName(workspace.bundles, activity.bundle_id) || 'No bundle'}
	                                    {activity.project_name ? ` · ${activity.project_name}` : ''}
	                                  </p>
	                                  {teamFieldValues.length > 0 && (
	                                    <div className="mt-2 flex flex-wrap gap-1.5">
	                                      {teamFieldValues.slice(0, 5).map(field => (
	                                        <Badge key={field.attr_id || field.name} variant="outline" className="max-w-full rounded-md">
	                                          <span className="truncate">
	                                            {field.name || 'Field'}: {formatTeamFieldValue(field.value)}
	                                          </span>
	                                        </Badge>
	                                      ))}
	                                      {teamFieldValues.length > 5 && (
	                                        <Badge variant="secondary" className="rounded-md">
	                                          +{teamFieldValues.length - 5}
	                                        </Badge>
	                                      )}
	                                    </div>
	                                  )}
	                                </div>
	                                <div className="truncate text-xs text-muted-foreground">
	                                  {activity.assigned_to_name || activity.assigned_to_email || memberName(workspace.members, activity.assigned_to_user_id)}
	                                </div>
	                                <div className="truncate text-xs text-muted-foreground">
	                                  {activity.due_date ? `Due ${formatDateTime(activity.due_date)}` : 'No due date'}
	                                </div>
	                                <div className="flex flex-wrap gap-2 md:justify-end">
	                                  <Badge variant="outline" className="rounded-md">
	                                    {activity.duration_minutes || 0}m
	                                  </Badge>
	                                  <Badge variant="secondary" className="rounded-md">
	                                    {activity.status}
	                                  </Badge>
	                                </div>
	                              </div>
	                            );
	                          })
	                        )}
                      </div>
                    </div>
	                  </CollapsibleSection>
                  </RetainedSection>)}
                </div>

                {visitedSections.includes('projects-bundles') && (<RetainedSection mode={routeSection === 'projects-bundles' ? 'visible' : 'hidden'}>
                  <>
                <CollapsibleSection
                  id="team-structure"
                  title="Project, bundle, and activity map"
                  description="See the team structure at a glance before assigning more work."
                  icon={FolderKanban}
                  collapsed={Boolean(collapsedSections.structure)}
                  onToggle={() => toggleSection('structure')}
                >
                  <div className="grid gap-3 lg:grid-cols-2">
                    {projectStructure.length === 0 ? (
                      <div className="rounded-md border bg-muted/25 p-4 text-sm text-muted-foreground">
                        No projects or bundles yet. Create a project or bundle below to give team activities a place to live.
                      </div>
                    ) : (
                      projectStructure.map(project => (
                        <div key={project.id} className="rounded-md border bg-background p-3 text-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate font-medium">{project.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {project.bundles.length} bundle{project.bundles.length === 1 ? '' : 's'} · {project.activityCount} activit{project.activityCount === 1 ? 'y' : 'ies'}
                              </p>
                            </div>
                            <Badge variant="outline" className="rounded-md">
                              P{project.priority_score ?? 0}
                            </Badge>
                          </div>
                          <div className="mt-3 space-y-2">
                            {project.bundles.length === 0 ? (
                              <p className="rounded-md border border-dashed p-2 text-xs text-muted-foreground">
                                No bundles under this project.
                              </p>
                            ) : (
                              project.bundles.map(bundle => (
                                <div key={bundle.id} className="rounded-md border bg-muted/20 p-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="truncate font-medium">{bundle.name}</span>
                                    <Badge variant="secondary" className="rounded-md">
                                      {bundle.activities.length}
                                    </Badge>
                                  </div>
                                  <div className="mt-2 space-y-1">
                                    {bundle.activities.slice(0, 4).map(activity => (
                                      <div key={activity.id} className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                                        <span className="truncate">{activity.title || 'Untitled activity'}</span>
                                        <span className="shrink-0">{activity.assigned_to_email || memberName(workspace.members, activity.assigned_to_user_id)}</span>
                                      </div>
                                    ))}
                                    {bundle.activities.length > 4 && (
                                      <p className="text-xs text-muted-foreground">
                                        +{bundle.activities.length - 4} more
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CollapsibleSection>

                <CollapsibleSection
                  id="team-projects"
                  title="Projects and bundles"
                  description="Create the structure that team activities belong to."
                  icon={FolderKanban}
                  collapsed={Boolean(collapsedSections.projects)}
                  onToggle={() => toggleSection('projects')}
                >
                  <div className="grid gap-3 xl:grid-cols-2">
                    <section className="rounded-md border bg-muted/10 p-3 sm:p-4">
                      <div className="mb-3">
                        <h3 className="text-base font-semibold">Projects</h3>
                        <p className="text-sm text-muted-foreground">Overarching purposes with team-level priority.</p>
                      </div>
                      <div className="space-y-3">
                      <div className="grid gap-2 sm:grid-cols-[1fr_90px_auto]">
                        <div className="space-y-1">
                          <Label htmlFor="teamProjectName">Project name</Label>
                          <Input
                            id="teamProjectName"
                            placeholder="Project name"
                            value={projectDraft.name}
                            onChange={event => setProjectDraft({ ...projectDraft, name: event.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="teamProjectPriority">Priority</Label>
                          <Input
                            id="teamProjectPriority"
                            type="number"
                            min="0"
                            max="10"
                            value={projectDraft.priority_score}
                            onChange={event =>
                              setProjectDraft({ ...projectDraft, priority_score: event.target.value })
                            }
                          />
                        </div>
                        <Button onClick={createProject}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="teamProjectPurpose">Purpose</Label>
                        <Textarea
                          id="teamProjectPurpose"
                          placeholder="Purpose"
                          value={projectDraft.purpose}
                          onChange={event => setProjectDraft({ ...projectDraft, purpose: event.target.value })}
                        />
                      </div>
	                      <div className="grid gap-2 sm:grid-cols-2">
	                        {(workspace.projects || []).map(project => (
	                          <div key={project.id} className="rounded-md border bg-background p-3 text-sm">
	                            {editingProjectId === project.id ? (
	                              <div className="space-y-3">
	                                <div className="grid gap-2 sm:grid-cols-[1fr_86px]">
	                                  <div className="space-y-1">
	                                    <Label htmlFor={`edit-project-name-${project.id}`}>Name</Label>
	                                    <Input
	                                      id={`edit-project-name-${project.id}`}
	                                      value={editingProjectDraft.name}
	                                      onChange={event =>
	                                        setEditingProjectDraft({
	                                          ...editingProjectDraft,
	                                          name: event.target.value,
	                                        })
	                                      }
	                                    />
	                                  </div>
	                                  <div className="space-y-1">
	                                    <Label htmlFor={`edit-project-priority-${project.id}`}>Priority</Label>
	                                    <Input
	                                      id={`edit-project-priority-${project.id}`}
	                                      type="number"
	                                      min="0"
	                                      max="10"
	                                      value={editingProjectDraft.priority_score}
	                                      onChange={event =>
	                                        setEditingProjectDraft({
	                                          ...editingProjectDraft,
	                                          priority_score: event.target.value,
	                                        })
	                                      }
	                                    />
	                                  </div>
	                                </div>
	                                <div className="space-y-1">
	                                  <Label htmlFor={`edit-project-purpose-${project.id}`}>Purpose</Label>
	                                  <Textarea
	                                    id={`edit-project-purpose-${project.id}`}
	                                    value={editingProjectDraft.purpose}
	                                    onChange={event =>
	                                      setEditingProjectDraft({
	                                        ...editingProjectDraft,
	                                        purpose: event.target.value,
	                                      })
	                                    }
	                                  />
	                                </div>
	                                <div className="flex flex-wrap justify-end gap-2">
	                                  <Button variant="ghost" size="sm" onClick={cancelEditingProject}>
	                                    Cancel
	                                  </Button>
	                                  <Button size="sm" onClick={() => saveProject(project.id)}>
	                                    <Check className="mr-2 h-4 w-4" />
	                                    Save
	                                  </Button>
	                                </div>
	                              </div>
	                            ) : (
	                              <>
	                                <div className="flex items-center justify-between gap-2">
	                                  <span className="truncate font-medium">{project.name}</span>
	                                  <Badge variant="outline" className="rounded-md">
	                                    P{project.priority_score ?? 0}
	                                  </Badge>
	                                </div>
	                                {project.purpose && (
	                                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
	                                    {project.purpose}
	                                  </p>
	                                )}
	                                <div className="mt-3 flex flex-wrap justify-end gap-2">
	                                  <Button variant="outline" size="sm" onClick={() => startEditingProject(project)}>
	                                    Rename
	                                  </Button>
	                                  <Button variant="ghost" size="sm" onClick={() => deleteProject(project)}>
	                                    <Trash2 className="mr-2 h-4 w-4" />
	                                    Delete
	                                  </Button>
	                                </div>
	                              </>
	                            )}
	                          </div>
	                        ))}
	                      </div>
                      </div>
                    </section>

                    <section className="rounded-md border bg-muted/10 p-3 sm:p-4">
                      <div className="mb-3">
                        <h3 className="text-base font-semibold">Bundles</h3>
                        <p className="text-sm text-muted-foreground">Every team activity belongs to one bundle.</p>
                      </div>
                      <div className="space-y-3">
                      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_90px_auto]">
                        <div className="space-y-1">
                          <Label htmlFor="teamBundleName">Bundle name</Label>
                          <Input
                            id="teamBundleName"
                            placeholder="Bundle name"
                            value={bundleDraft.name}
                            onChange={event => setBundleDraft({ ...bundleDraft, name: event.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Project</Label>
                          <Select
                            value={bundleDraft.project_id}
                            onValueChange={project_id => setBundleDraft({ ...bundleDraft, project_id })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No project</SelectItem>
                              {(workspace.projects || []).map(project => (
                                <SelectItem key={project.id} value={project.id}>
                                  {project.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="teamBundlePriority">Priority</Label>
                          <Input
                            id="teamBundlePriority"
                            type="number"
                            min="0"
                            max="10"
                            value={bundleDraft.priority_score}
                            onChange={event =>
                              setBundleDraft({ ...bundleDraft, priority_score: event.target.value })
                            }
                          />
                        </div>
                        <Button onClick={createBundle}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="teamBundlePurpose">Purpose</Label>
                        <Textarea
                          id="teamBundlePurpose"
                          placeholder="Purpose"
                          value={bundleDraft.purpose}
                          onChange={event => setBundleDraft({ ...bundleDraft, purpose: event.target.value })}
                        />
                      </div>
	                      <div className="space-y-3">
	                        {bundleManagerGroups.length === 0 ? (
	                          <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
	                            No bundles yet.
	                          </div>
	                        ) : (
	                          bundleManagerGroups.map(group => (
	                            <div key={group.id} className="rounded-md border bg-background p-3">
	                              <div className="mb-2 flex items-center justify-between gap-2 text-sm">
	                                <span className="truncate font-medium">{group.label}</span>
	                                <Badge variant={group.projectId ? 'outline' : 'secondary'} className="rounded-md">
	                                  {group.bundles.length}
	                                </Badge>
	                              </div>
	                              <div className="grid gap-2">
	                                {group.bundles.map(bundle => (
	                                  <div key={bundle.id} className="rounded-md border bg-muted/10 p-3 text-sm">
	                                    {editingBundleId === bundle.id ? (
	                                      <div className="space-y-3">
	                                        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_86px]">
	                                          <div className="space-y-1">
	                                            <Label htmlFor={`edit-bundle-name-${bundle.id}`}>Name</Label>
	                                            <Input
	                                              id={`edit-bundle-name-${bundle.id}`}
	                                              value={editingBundleDraft.name}
	                                              onChange={event =>
	                                                setEditingBundleDraft({
	                                                  ...editingBundleDraft,
	                                                  name: event.target.value,
	                                                })
	                                              }
	                                            />
	                                          </div>
	                                          <div className="space-y-1">
	                                            <Label>Project</Label>
	                                            <Select
	                                              value={editingBundleDraft.project_id}
	                                              onValueChange={project_id =>
	                                                setEditingBundleDraft({
	                                                  ...editingBundleDraft,
	                                                  project_id,
	                                                })
	                                              }
	                                            >
	                                              <SelectTrigger>
	                                                <SelectValue />
	                                              </SelectTrigger>
	                                              <SelectContent>
	                                                <SelectItem value="none">No project</SelectItem>
	                                                {(workspace.projects || []).map(project => (
	                                                  <SelectItem key={project.id} value={project.id}>
	                                                    {project.name}
	                                                  </SelectItem>
	                                                ))}
	                                              </SelectContent>
	                                            </Select>
	                                          </div>
	                                          <div className="space-y-1">
	                                            <Label htmlFor={`edit-bundle-priority-${bundle.id}`}>Priority</Label>
	                                            <Input
	                                              id={`edit-bundle-priority-${bundle.id}`}
	                                              type="number"
	                                              min="0"
	                                              max="10"
	                                              value={editingBundleDraft.priority_score}
	                                              onChange={event =>
	                                                setEditingBundleDraft({
	                                                  ...editingBundleDraft,
	                                                  priority_score: event.target.value,
	                                                })
	                                              }
	                                            />
	                                          </div>
	                                        </div>
	                                        <div className="space-y-1">
	                                          <Label htmlFor={`edit-bundle-purpose-${bundle.id}`}>Purpose</Label>
	                                          <Textarea
	                                            id={`edit-bundle-purpose-${bundle.id}`}
	                                            value={editingBundleDraft.purpose}
	                                            onChange={event =>
	                                              setEditingBundleDraft({
	                                                ...editingBundleDraft,
	                                                purpose: event.target.value,
	                                              })
	                                            }
	                                          />
	                                        </div>
	                                        <div className="flex flex-wrap justify-end gap-2">
	                                          <Button variant="ghost" size="sm" onClick={cancelEditingBundle}>
	                                            Cancel
	                                          </Button>
	                                          <Button size="sm" onClick={() => saveBundle(bundle.id)}>
	                                            <Check className="mr-2 h-4 w-4" />
	                                            Save
	                                          </Button>
	                                        </div>
	                                      </div>
	                                    ) : (
	                                      <>
	                                        <div className="flex items-center justify-between gap-2">
	                                          <span className="truncate font-medium">{bundle.name}</span>
	                                          <Badge variant="outline" className="rounded-md">
	                                            P{bundle.priority_score ?? 0}
	                                          </Badge>
	                                        </div>
	                                        <p className="mt-1 truncate text-xs text-muted-foreground">
	                                          {projectName(workspace.projects, bundle.project_id) || 'No project'}
	                                        </p>
	                                        {bundle.purpose && (
	                                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
	                                            {bundle.purpose}
	                                          </p>
	                                        )}
	                                        <div className="mt-3 flex flex-wrap justify-end gap-2">
	                                          <Button variant="outline" size="sm" onClick={() => startEditingBundle(bundle)}>
	                                            Rename
	                                          </Button>
	                                          <Button variant="ghost" size="sm" onClick={() => deleteBundle(bundle)}>
	                                            <Trash2 className="mr-2 h-4 w-4" />
	                                            Delete
	                                          </Button>
	                                        </div>
	                                      </>
	                                    )}
	                                  </div>
	                                ))}
	                              </div>
	                            </div>
	                          ))
	                        )}
	                      </div>
                      </div>
	                    </section>
	                  </div>
	                </CollapsibleSection>
                  </>
                </RetainedSection>)}

                {visitedSections.includes('fields') && (<RetainedSection mode={routeSection === 'fields' ? 'visible' : 'hidden'}>
                <CollapsibleSection
                  id="team-fields"
                  title="Team custom fields"
                  description="Fields here apply to team activities. Required fields must be filled before assignment."
                  icon={SlidersHorizontal}
                  collapsed={Boolean(collapsedSections.fields)}
                  onToggle={() => toggleSection('fields')}
                >
                  <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(260px,0.75fr)]">
                    <div className="space-y-3">
                      <div className="grid gap-2 md:grid-cols-[1fr_130px_110px_110px_auto]">
                        <div className="space-y-1">
                          <Label htmlFor="teamFieldName">Field name</Label>
                          <Input
                            id="teamFieldName"
                            value={attributeDraft.name}
                            onChange={event => setAttributeDraft({ ...attributeDraft, name: event.target.value })}
                            placeholder="Client impact"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label>Type</Label>
                          <Select
                            value={attributeDraft.data_type}
                            onValueChange={data_type => setAttributeDraft({ ...attributeDraft, data_type })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="float">Number</SelectItem>
                              <SelectItem value="integer">Integer</SelectItem>
                              <SelectItem value="enum">Dropdown</SelectItem>
                              <SelectItem value="string">Text</SelectItem>
                              <SelectItem value="boolean">Boolean</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="teamFieldSpecificity">Specificity</Label>
                          <Input
                            id="teamFieldSpecificity"
                            type="number"
                            min="0"
                            max="10"
                            step="0.1"
                            value={attributeDraft.specificity}
                            onChange={event => setAttributeDraft({ ...attributeDraft, specificity: event.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="teamFieldWeight">Weight</Label>
                          <Input
                            id="teamFieldWeight"
                            type="number"
                            min="0"
                            max="10"
                            step="0.1"
                            value={attributeDraft.field_weight}
                            onChange={event => setAttributeDraft({ ...attributeDraft, field_weight: event.target.value })}
                          />
                        </div>
                        <div className="flex items-end">
                          <Button className="w-full" onClick={createTeamAttribute}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add
                          </Button>
                        </div>
                      </div>
                      {attributeDraft.data_type === 'enum' && (
                        <div className="space-y-1">
                          <Label htmlFor="teamFieldOptions">Dropdown options</Label>
                          <Textarea
                            id="teamFieldOptions"
                            className="min-h-20"
                            value={attributeDraft.options}
                            onChange={event => setAttributeDraft({ ...attributeDraft, options: event.target.value })}
                            placeholder="One option per line or comma separated"
                          />
                        </div>
                      )}
                      <label className="flex items-start gap-3 rounded-md border bg-muted/30 p-3 text-sm">
                        <Checkbox
                          checked={attributeDraft.required}
                          onCheckedChange={checked =>
                            setAttributeDraft({ ...attributeDraft, required: Boolean(checked) })
                          }
                        />
                        <span>
                          <span className="block font-medium">Required before team assignment</span>
                          <span className="block text-xs text-muted-foreground">
                            Optional fields still train the model. Required fields block creation until filled.
                          </span>
                        </span>
                      </label>
                    </div>
                    <div className="divide-y rounded-md border">
                      {teamAttributes.length === 0 ? (
                        <p className="p-3 text-sm text-muted-foreground">No team fields yet.</p>
                      ) : (
                        teamAttributes.map(attribute => (
                          <div key={attribute.attr_id} className="flex items-center justify-between gap-3 p-3 text-sm">
                            <div className="min-w-0">
                              <p className="truncate font-medium">{attribute.name}</p>
                              <p className="truncate text-xs text-muted-foreground">
                                {attribute.data_type} · specificity {attribute.specificity ?? 5} · weight {attribute.field_weight ?? 1}
                                {attribute.required ? ' · required' : ' · optional'}
                              </p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => deleteTeamAttribute(attribute.attr_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </CollapsibleSection>
                </RetainedSection>)}

                <div className="grid gap-3 xl:grid-cols-2">
                  {visitedSections.includes('intake') && (<RetainedSection mode={routeSection === 'intake' ? 'visible' : 'hidden'}>
                  <CollapsibleSection
                    id="team-intake"
                    title="Email Intake Rules"
                    description="Rules define which messages should become team proposals."
                    icon={Mail}
                    collapsed={Boolean(collapsedSections.intake)}
                    onToggle={() => toggleSection('intake')}
                  >
                    <div className="space-y-3">
                      <div className="grid gap-2 sm:grid-cols-[120px_1fr_150px_auto]">
                        <Select
                          value={ruleDraft.rule_type}
                          onValueChange={rule_type => setRuleDraft({ ...ruleDraft, rule_type })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="keyword">Keyword</SelectItem>
                            <SelectItem value="sender">Sender</SelectItem>
                            <SelectItem value="domain">Domain</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Pattern"
                          value={ruleDraft.pattern}
                          onChange={event => setRuleDraft({ ...ruleDraft, pattern: event.target.value })}
                        />
                        <Select
                          value={ruleDraft.action}
                          onValueChange={action => setRuleDraft({ ...ruleDraft, action })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="propose_activity">Propose</SelectItem>
                            <SelectItem value="create_activity">Create</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button onClick={createEmailRule}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Select
                          value={ruleDraft.project_id}
                          onValueChange={project_id => setRuleDraft({ ...ruleDraft, project_id })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No project default</SelectItem>
                            {(workspace.projects || []).map(project => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={ruleDraft.bundle_id}
                          onValueChange={bundle_id => setRuleDraft({ ...ruleDraft, bundle_id })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No bundle default</SelectItem>
                            {(workspace.bundles || []).map(bundle => (
                              <SelectItem key={bundle.id} value={bundle.id}>
                                {bundle.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="divide-y rounded-md border">
                        {(workspace.email_rules || []).map(rule => (
                          <div key={rule.id} className="flex flex-col gap-3 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                              <p className="truncate font-medium">
                                {rule.rule_type}: {rule.pattern}
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {rule.action.replace('_', ' ')}
                              </p>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => deleteEmailRule(rule.id)}>
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleSection>
                  </RetainedSection>)}

	                  {visitedSections.includes('integrations') && (<RetainedSection mode={routeSection === 'integrations' ? 'visible' : 'hidden'}>
	                  <CollapsibleSection
                      id="team-integrations"
                      title="Calendar integrations"
                      description="Generate scoped subscription calendars and connect other Fluxability accounts with chosen visibility."
                      icon={CalendarDays}
                      collapsed={Boolean(collapsedSections.integrations)}
                      onToggle={() => toggleSection('integrations')}
                    >
	                    <div className="space-y-3 sm:space-y-4">
	                      <div className="space-y-3 rounded-md border p-3">
	                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
	                          <div>
	                            <h3 className="text-sm font-medium">Create subscription calendar</h3>
	                            <p className="text-xs text-muted-foreground">
	                              New feed URLs are shown once. Revoke and recreate a feed if the token needs to rotate.
	                            </p>
	                          </div>
	                          <Button className="w-full sm:w-auto" onClick={createFeed}>
	                            <Plus className="mr-2 h-4 w-4" />
	                            Create feed
	                          </Button>
	                        </div>
	                        <div className="grid gap-2 sm:grid-cols-2">
	                          <Select
	                            value={feedDraft.permission}
	                            onValueChange={permission => setFeedDraft({ ...feedDraft, permission })}
	                          >
	                            <SelectTrigger>
	                              <SelectValue />
	                            </SelectTrigger>
	                            <SelectContent>
	                              {feedPermissions.map(permission => (
	                                <SelectItem key={permission.value} value={permission.value}>
	                                  {permission.label}
	                                </SelectItem>
	                              ))}
	                            </SelectContent>
	                          </Select>
	                          <Input
	                            placeholder="Feed label"
	                            value={feedDraft.label}
	                            onChange={event => setFeedDraft({ ...feedDraft, label: event.target.value })}
	                          />
	                        </div>
	                        <label className="flex items-start gap-3 rounded-md border bg-muted/30 p-3 text-sm">
	                          <Checkbox
	                            checked={feedDraft.include_gcal}
	                            onCheckedChange={checked =>
	                              setFeedDraft({ ...feedDraft, include_gcal: Boolean(checked) })
	                            }
	                          />
	                          <span>
	                            <span className="block font-medium">Pass selected Google Calendar commitments</span>
	                            <span className="block text-xs text-muted-foreground">
	                              This shares synced commitments from this account using the privacy level below.
	                            </span>
	                          </span>
	                        </label>
	                        {feedDraft.include_gcal && (
	                          <div className="grid gap-2 sm:grid-cols-[180px_1fr]">
	                            <Select
	                              value={feedDraft.gcal_visibility}
	                              onValueChange={gcal_visibility =>
	                                setFeedDraft({ ...feedDraft, gcal_visibility })
	                              }
	                            >
	                              <SelectTrigger>
	                                <SelectValue />
	                              </SelectTrigger>
	                              <SelectContent>
	                                {gcalVisibilityOptions.map(option => (
	                                  <SelectItem key={option.value} value={option.value}>
	                                    {option.label}
	                                  </SelectItem>
	                                ))}
	                              </SelectContent>
	                            </Select>
	                            <Textarea
	                              className="min-h-10"
	                              placeholder="Optional calendar IDs, comma or line separated"
	                              value={feedDraft.calendar_ids}
	                              onChange={event =>
	                                setFeedDraft({ ...feedDraft, calendar_ids: event.target.value })
	                              }
	                            />
	                          </div>
	                        )}
	                      </div>

	                      {latestFeedUrl && (
	                        <button
	                          type="button"
	                          className="flex w-full items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-left text-xs"
	                          onClick={() => navigator.clipboard?.writeText(latestFeedUrl)}
	                        >
	                          <Copy className="h-4 w-4 shrink-0" />
	                          <span className="truncate">{latestFeedUrl}</span>
	                        </button>
	                      )}

	                      <div className="divide-y rounded-md border">
	                        {(workspace.feeds || []).length === 0 ? (
	                          <p className="p-3 text-sm text-muted-foreground">No active calendar feeds.</p>
	                        ) : (
	                          (workspace.feeds || []).map(feed => (
	                            <div key={feed.id} className="flex flex-col gap-3 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
	                              <div className="min-w-0">
	                                <p className="truncate font-medium">{feed.label}</p>
	                                <p className="truncate text-xs text-muted-foreground">
	                                  Team: {feed.permission?.replaceAll('_', ' ')}
	                                  {feed.include_gcal
	                                    ? ` · GCal: ${feed.gcal_visibility?.replaceAll('_', ' ')}`
	                                    : ' · GCal off'}
	                                </p>
	                              </div>
	                              <Button variant="outline" size="sm" onClick={() => revokeFeed(feed.id)}>
	                                <Trash2 className="h-4 w-4" />
	                              </Button>
	                            </div>
	                          ))
	                        )}
	                      </div>

	                      <div className="space-y-3 rounded-md border p-3">
	                        <div>
	                          <h3 className="flex items-center gap-2 text-sm font-medium">
                              <GitBranch className="h-4 w-4" />
                              Integrate with Other Fluxability Accounts
                            </h3>
	                          <p className="text-xs text-muted-foreground">
		                            Administrators can link personal and company instances while choosing what team and calendar data crosses the boundary.
	                          </p>
	                        </div>
	                        <div className="grid gap-2 sm:grid-cols-[150px_1fr_140px]">
	                          <Select
	                            value={connectionDraft.provider}
	                            onValueChange={provider => setConnectionDraft({ ...connectionDraft, provider })}
	                            disabled={!isAdmin}
	                          >
	                            <SelectTrigger>
	                              <SelectValue />
	                            </SelectTrigger>
	                            <SelectContent>
	                              <SelectItem value="flexiplanner">Fluxability</SelectItem>
	                              <SelectItem value="saimail">SaiMail</SelectItem>
	                              <SelectItem value="mail">Mail</SelectItem>
	                              <SelectItem value="zoho">Zoho</SelectItem>
	                              <SelectItem value="github">GitHub</SelectItem>
	                            </SelectContent>
	                          </Select>
	                          <Input
	                            placeholder="remote domain"
	                            value={connectionDraft.remote_domain}
	                            disabled={!isAdmin}
	                            onChange={event =>
	                              setConnectionDraft({ ...connectionDraft, remote_domain: event.target.value })
	                            }
	                          />
	                          <Select
	                            value={connectionDraft.direction}
	                            onValueChange={direction => setConnectionDraft({ ...connectionDraft, direction })}
	                            disabled={!isAdmin}
	                          >
	                            <SelectTrigger>
	                              <SelectValue />
	                            </SelectTrigger>
	                            <SelectContent>
	                              {syncPolicies.map(policy => (
	                                <SelectItem key={policy.value} value={policy.value}>
	                                  {policy.label}
	                                </SelectItem>
	                              ))}
	                            </SelectContent>
	                          </Select>
	                        </div>
	                        <Input
	                          placeholder="Remote account email"
	                          value={connectionDraft.remote_account_email}
	                          disabled={!isAdmin}
	                          onChange={event =>
	                            setConnectionDraft({ ...connectionDraft, remote_account_email: event.target.value })
	                          }
	                        />
	                        <div className="grid gap-2 sm:grid-cols-2">
	                          <Select
	                            value={connectionDraft.team_visibility}
	                            onValueChange={team_visibility =>
	                              setConnectionDraft({ ...connectionDraft, team_visibility })
	                            }
	                            disabled={!isAdmin}
	                          >
	                            <SelectTrigger>
	                              <SelectValue />
	                            </SelectTrigger>
	                            <SelectContent>
	                              {feedPermissions.map(permission => (
	                                <SelectItem key={permission.value} value={permission.value}>
	                                  {permission.label}
	                                </SelectItem>
	                              ))}
	                            </SelectContent>
	                          </Select>
	                          <label className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm">
	                            <Checkbox
	                              checked={connectionDraft.share_gcal}
	                              disabled={!isAdmin}
	                              onCheckedChange={checked =>
	                                setConnectionDraft({ ...connectionDraft, share_gcal: Boolean(checked) })
	                              }
	                            />
	                            Pass selected Google calendars
	                          </label>
	                        </div>
	                        {connectionDraft.share_gcal && (
	                          <div className="grid gap-2 sm:grid-cols-[180px_1fr]">
	                            <Select
	                              value={connectionDraft.gcal_visibility}
	                              onValueChange={gcal_visibility =>
	                                setConnectionDraft({ ...connectionDraft, gcal_visibility })
	                              }
	                              disabled={!isAdmin}
	                            >
	                              <SelectTrigger>
	                                <SelectValue />
	                              </SelectTrigger>
	                              <SelectContent>
	                                {gcalVisibilityOptions.map(option => (
	                                  <SelectItem key={option.value} value={option.value}>
	                                    {option.label}
	                                  </SelectItem>
	                                ))}
	                              </SelectContent>
	                            </Select>
	                            <Textarea
	                              className="min-h-10"
	                              placeholder="Optional calendar IDs for this link"
	                              value={connectionDraft.calendar_ids}
	                              disabled={!isAdmin}
	                              onChange={event =>
	                                setConnectionDraft({ ...connectionDraft, calendar_ids: event.target.value })
	                              }
	                            />
	                          </div>
	                        )}
	                        <Button className="w-full sm:w-auto" onClick={createConnection} disabled={!isAdmin}>
	                          <Link className="mr-2 h-4 w-4" />
	                          Save integration
	                        </Button>
	                      </div>

	                      <div className="divide-y rounded-md border">
	                        {(workspace.connections || []).length === 0 ? (
	                          <p className="p-3 text-sm text-muted-foreground">No saved integrations.</p>
	                        ) : (workspace.connections || []).map(connection => (
	                          <div key={connection.id} className="flex flex-col gap-3 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
	                            <div className="min-w-0">
	                              <p className="truncate font-medium">
	                                {connection.provider} {connection.remote_domain ? `-> ${connection.remote_domain}` : ''}
	                              </p>
	                              <p className="truncate text-xs text-muted-foreground">
	                                {connection.direction}
	                                {connection.metadata?.share_gcal
	                                  ? ` · GCal ${connection.metadata?.gcal_visibility || 'busy_free'}`
	                                  : ' · GCal off'}
	                              </p>
	                            </div>
	                            <div className="flex shrink-0 items-center gap-2">
	                              <Badge variant="secondary" className="rounded-md">
	                                <Check className="mr-1 h-3 w-3" />
	                                {connection.status}
	                              </Badge>
	                              <Button
	                                variant="outline"
	                                size="sm"
	                                onClick={() => deleteConnection(connection.id)}
	                                disabled={!isAdmin}
	                              >
	                                <Trash2 className="h-4 w-4" />
	                              </Button>
	                            </div>
	                          </div>
	                        ))}
	                      </div>
	                    </div>
	                  </CollapsibleSection>
                    </RetainedSection>)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Render a controlled team-settings disclosure with a stable section ID. The owner retains collapse state; expanding only changes presentation. */
function CollapsibleSection({
  id,
  title,
  description,
  icon: Icon,
  collapsed,
  onToggle,
  children,
}: {
  /** Stable DOM section ID used to identify this disclosure. */
  id: string;
  /** Visible heading for this panel or collection. */
  title: string;
  /** Optional explanatory text beneath the panel heading. */
  description?: string;
  /** Icon component rendered beside the visible caption. */
  icon: LucideIcon;
  /** Whether the section body is hidden. */
  collapsed: boolean;
  /** Ask the owner to toggle this section’s collapsed state. */
  onToggle: () => void;
  /** Controls or content supplied by the owning view. */
  children: ReactNode;
}) {
  return (
    <Card id={id} className="overflow-hidden rounded-md border bg-card/95 text-card-foreground shadow-sm">
      <CardHeader className="p-0">
        <button
          type="button"
          className="flex w-full items-start justify-between gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-accent/50 sm:px-4"
          onClick={onToggle}
          aria-expanded={!collapsed}
        >
          <span className="flex min-w-0 items-start gap-2">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span className="min-w-0">
              <CardTitle className="text-base">{title}</CardTitle>
              {description && <CardDescription className="mt-1">{description}</CardDescription>}
            </span>
          </span>
          {collapsed ? (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </button>
      </CardHeader>
      {!collapsed && <CardContent className="p-3 pt-0 sm:p-4 sm:pt-0">{children}</CardContent>}
    </Card>
  );
}

/** Render an enum, boolean, number, date, or text control from a team attribute definition. Preserve a string draft until the enclosing team mutation validates it. */
function TeamAttributeInput({
  attribute,
  value,
  onChange,
}: {
  /** Team field definition controlling the label, type, choices, and allowed bounds. */
  attribute: TeamCustomField;
  /** Controlled field draft, kept as a string until the enclosing form parses and validates it. */
  value: string;
  /** Report the edited draft value to the owner; this callback does not itself prove a saved server result. */
  onChange: (value: string) => void;
}) {
  const label = `${attribute.name}${attribute.required ? ' *' : ''}`;
  const options = parseAttributeOptions(attribute.options);
  if (attribute.data_type === 'enum') {
    return (
      <div className="space-y-1">
        <Label>{label}</Label>
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder={`Select ${attribute.name}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (attribute.data_type === 'boolean') {
    return (
      <label className="flex min-h-10 items-center gap-3 rounded-md border bg-background px-3 py-2 text-sm">
        <Checkbox checked={value === 'true'} onCheckedChange={checked => onChange(checked ? 'true' : 'false')} />
        <span>{label}</span>
      </label>
    );
  }
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        type={['float', 'integer'].includes(attribute.data_type) ? 'number' : attribute.data_type === 'date' ? 'date' : 'text'}
        value={value}
        onChange={event => onChange(event.target.value)}
        min={attribute.min_val ?? undefined}
        max={attribute.max_val ?? undefined}
      />
    </div>
  );
}

/** Resolve a route section or retained alias to the canonical team-section key. Unknown or absent values return undefined. */
function normalizeTeamSection(value?: string): TeamSectionKey | undefined {
  if (!value) return undefined;
  return teamSectionAliases[value] || teamSectionAliases[value.toLowerCase()];
}

/** Build an encoded selected-team overview path, or the team chooser path when no team is selected. */
function teamOverviewPath(teamId?: string | null) {
  return teamId ? `/teams/${encodeURIComponent(teamId)}` : '/teams';
}

/** Build an encoded team path for an already validated section key. */
function teamSectionPath(teamId: string, section: TeamSectionKey) {
  return `/teams/${encodeURIComponent(teamId)}/${section}`;
}

/** Apply the initial route to the default disclosure map so linked content starts expanded. */
function initialCollapsedSections(routeSection?: TeamSectionKey) {
  return openSectionsForRoute(defaultCollapsedSections, routeSection);
}

/** Copy disclosure state and expand the panels belonging to the selected route. Without a route section, overview and structure open by default; unrelated disclosure choices are retained. */
function openSectionsForRoute(
  previous: Record<string, boolean>,
  routeSection?: TeamSectionKey
): Record<string, boolean> {
  const next: Record<string, boolean> = { ...previous };
  next.overview = routeSection ? Boolean(previous.overview) : false;
  next.structure = routeSection ? Boolean(previous.structure) : false;
  const section = teamSections.find(item => item.id === routeSection);
  if (section) {
    for (const sectionId of section.sectionIds) {
      next[sectionId] = false;
    }
  }
  return next;
}

/** Resolve a project display name from the loaded workspace; an absent or unknown project returns an empty label. */
function projectName(projects: WorkspaceProject[] = [], projectId?: string | null) {
  return projects.find(project => project.id === projectId)?.name || '';
}

/** Resolve a bundle display name from the loaded workspace; an absent or unknown bundle returns an empty label. */
function bundleName(bundles: WorkspaceBundle[] = [], bundleId?: string | null) {
  return bundles.find(bundle => bundle.id === bundleId)?.name || '';
}

/** Resolve a user’s display name, then email, from team membership records; unknown or absent identities produce an empty label. */
function memberName(members: TeamMember[] = [], userId?: string | null) {
  if (!userId) return '';
  const member = members.find(item => item.user_id === userId);
  return member?.display_name || member?.email || '';
}

/** Extract a normalized domain string for UI matching. This is not an email validator or mailbox-ownership proof. */
function emailDomain(email?: string | null) {
  return String(email || '')
    .trim()
    .toLowerCase()
    .split('@')[1] || '';
}

/** Format the returned provisioning outcome, distinguishing a new account, an existing account, an admin promotion, failure, and an explicit skip. Text alone does not verify live mailbox access. */
function mailProvisioningStatus(result?: MailProvisioningOutcome | null) {
  if (!result) return '';
  if (result.status === 'attached') {
    if (result.already_exists && result.promoted_admin) return `Mail account ${result.email} was promoted to admin.`;
    if (result.already_exists) return `Mail account ${result.email} already exists.`;
    return `Mail account created for ${result.email}${result.role === 'admin' ? ' as an admin' : ''}.`;
  }
  if (result.status === 'failed') return `Mail provisioning failed for ${result.email}: ${result.message}`;
  if (result.status === 'skipped' && result.message) return result.message;
  return '';
}

/** Format a valid timestamp in local month/day/time form; preserve an unparseable nonempty string so the source value remains inspectable. */
function formatDateTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Render custom values without expanding arbitrary object contents: empty values get an empty label, booleans become Yes/No, arrays recurse, and objects display set. */
function formatTeamFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'empty';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(formatTeamFieldValue).join(', ');
  if (typeof value === 'object') return 'set';
  return String(value);
}

/** Escape ampersands, angle brackets, and quotes before interpolating a value into generated HTML. This encodes text; it does not validate URLs or grant access. */
function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

/** Split comma/newline calendar IDs, trim blanks, and preserve first occurrence order while removing duplicates. */
function parseCalendarIds(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[\n,]/)
        .map(item => item.trim())
        .filter(Boolean)
    )
  );
}

/** Normalize a stored object or JSON policy for display. Invalid JSON becomes an empty policy and only the literal restricted mode is retained; server policy remains authoritative. */
function parseConnectionPolicy(value: unknown) {
  let parsed: unknown = value;
  if (typeof parsed === 'string') {
    try {
      parsed = parsed ? JSON.parse(parsed) : {};
    } catch {
      parsed = {};
    }
  }
  const policy = parsed as { /** Allowed partner domains. */ allowed_domains?: unknown; /** `open` or `restricted`. */ mode?: unknown } | null | undefined;
  const allowedDomains: string[] = Array.isArray(policy?.allowed_domains)
    ? policy.allowed_domains.map((item: unknown) => String(item).trim()).filter(Boolean)
    : [];
  return {
    mode: policy?.mode === 'restricted' ? 'restricted' : 'open',
    allowed_domains: allowedDomains,
  };
}

/** Normalize comma/newline policy entries to lowercase, trimmed, unique strings. */
function parsePolicyList(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[\n,]/)
        .map(item => item.trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

/** Present the manager role as Administrator and other role values as Member; this label does not decide authorization. */
function roleLabel(role?: string) {
  return role === 'manager' ? 'Administrator' : 'Member';
}

/** Map public/private visibility to its label and use Invite-only for other values. */
function visibilityLabel(visibility?: string) {
  if (visibility === 'public') return 'Public';
  if (visibility === 'private') return 'Private';
  return 'Invite-only';
}

/** Choose the icon matching the visibility label; unknown values use the invite-only icon. */
function visibilityIcon(visibility?: string) {
  if (visibility === 'public') return <Globe2 className="h-3.5 w-3.5 shrink-0" />;
  if (visibility === 'private') return <Lock className="h-3.5 w-3.5 shrink-0" />;
  return <UserCheck className="h-3.5 w-3.5 shrink-0" />;
}

/** A bundle row of the project overview: a stored bundle, or a holder for activities whose bundle is not listed. */
type StructureBundle = {
  /** Bundle ID, or `unbundled-<activity id>` for a holder. */
  id: string;
  /** Bundle name. */
  name: string;
  /** Activities in the bundle. */
  activities: TeamActivityView[];
};

/** A project row of the project overview, including the synthetic `no-project` row. */
type StructureProject = {
  /** Project ID, or `no-project`. */
  id: string;
  /** Project name. */
  name: string;
  /** Sort priority; higher first. */
  priority_score?: number | null;
  /** Bundles under the project. */
  bundles: StructureBundle[];
  /** Activities across the project's bundles. */
  activityCount: number;
};

/** Index projects and bundles once, attach activities by bundle, and retain orphaned work under synthetic unbundled rows. Count activities and sort projects by priority without mutating the API arrays. */
function buildProjectStructure(
  projects: WorkspaceProject[] = [],
  bundles: WorkspaceBundle[] = [],
  activities: TeamActivityView[] = []
): StructureProject[] {
  const projectMap = new Map<string, StructureProject>();
  const noProjectId = 'no-project';
  for (const project of projects) {
    projectMap.set(project.id, {
      ...project,
      bundles: [],
      activityCount: 0,
    });
  }
  projectMap.set(noProjectId, {
    id: noProjectId,
    name: 'No project',
    priority_score: 0,
    bundles: [],
    activityCount: 0,
  });

  const bundleMap = new Map<string, StructureBundle>();
  for (const bundle of bundles) {
    const projectId = bundle.project_id && projectMap.has(bundle.project_id) ? bundle.project_id : noProjectId;
    const normalized: StructureBundle = { ...bundle, activities: [] };
    bundleMap.set(bundle.id, normalized);
    projectMap.get(projectId)?.bundles.push(normalized);
  }

  for (const activity of activities) {
    const bundle = activity.bundle_id ? bundleMap.get(activity.bundle_id) : undefined;
    if (bundle) {
      bundle.activities.push(activity);
    } else {
      const fallback: StructureBundle = {
        id: `unbundled-${activity.id}`,
        name: activity.bundle_name || 'Unbundled',
        activities: [activity],
      };
      projectMap.get(noProjectId)?.bundles.push(fallback);
    }
  }

  for (const project of projectMap.values()) {
    project.activityCount = project.bundles.reduce(
      (sum: number, bundle) => sum + (bundle.activities?.length || 0),
      0
    );
  }

  return [...projectMap.values()]
    .filter(project => project.id !== noProjectId || project.bundles.length > 0 || projects.length === 0)
    .sort((a, b) => Number(b.priority_score || 0) - Number(a.priority_score || 0));
}

/** Build nonempty custom-field values under both stable attribute IDs and normalized name aliases for wire compatibility. Coercion is shallow; server field validation remains authoritative. */
function buildTeamActivityAttributes(attributes: TeamCustomField[] = [], values: Record<string, string>) {
  const output: Record<string, unknown> = {};
  for (const attribute of attributes) {
    const raw = values[attribute.attr_id];
    if (raw === undefined || raw === '') continue;
    const value = coerceAttributeValue(attribute, raw);
    output[attribute.attr_id] = value;
    output[normalizeAttributeKey(attribute.name)] = value;
  }
  return output;
}

/** Convert supported numeric drafts when finite and boolean drafts by the literal true value. Unparseable numbers remain strings for downstream validation rather than silently becoming zero. */
function coerceAttributeValue(attribute: TeamCustomField, value: string): unknown {
  if (['float', 'integer'].includes(attribute.data_type)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : value;
  }
  if (attribute.data_type === 'boolean') return value === 'true';
  return value;
}

/** Convert a field name to a lowercase ASCII underscore key, trimming separator runs at both ends for compatibility aliases. */
function normalizeAttributeKey(name: unknown): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Read enum choices from arrays, JSON array strings, or comma/newline-separated legacy text. Trim empty options; malformed JSON falls through to delimited parsing. */
function parseAttributeOptions(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(option => String(option).trim()).filter(Boolean);
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(option => String(option).trim()).filter(Boolean);
  } catch {
    // Fall through to delimited parsing.
  }
  return value
    .split(/[\n,]/)
    .map(option => option.trim())
    .filter(Boolean);
}
